import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import {z as zod} from "zod";

/**
 * Workspace UI config types (pages, forms, CTAs, scaffold, …).
 * Nested shape for `workspace_ui_config` / {@link WorkspaceUiConfigConfiguration}.
 *
 * Prefer Uuid discriminants, `|` / `&` splits, and reuse of
 * {@link FilterItem} / {@link Conditions} everywhere. No legacy aliases.
 */

export type SortItem = {
    fieldId: Uuid;
    direction: "ascending" | "descending";
};

export type FilterOperator =
    | "lessThan"
    | "greaterThan"
    | "equals"
    | "notEquals"
    | "in"
    | "contains"
    | "greaterThanEqualTo"
    | "lessThanEqualTo";

/** Single-value operators — use `value`. */
type FilterOperatorWithValue = Exclude<FilterOperator, "in">;

/**
 * Field match clause.
 * - `equals` / `notEquals` / `contains` / comparisons → `value`
 * - `in` → `values` (array)
 * - `userTemplate` → signed-in user's template id is in `values`
 */
export type FilterItem =
    | {
          type: "singleValue";
          fieldId: Uuid;
          operator: FilterOperatorWithValue;
          value: string | null;
      }
    | {
          type: "multipleValues";
          fieldId: Uuid;
          operator: "in";
          values: Array<string>;
      }
    | {
          type: "duration";
          fieldId: Uuid;
          operator: "lessThanEqualTo" | "greaterThanEqualTo" | "lessThan" | "greaterThan" | "equals";
          duration: number;
      }
    | {
          /**
           * Form-session predicate on a configured field (e.g. account selector committed).
           * Used in FormField `readonlyWhen` / `requiredWhen` / `visibleWhen` — not list/CTA filters.
           */
          type: "formFieldState";
          fieldId: Uuid;
          state: "accountCommitted";
          operator: "equals" | "notEquals";
          value: "true" | "false";
      }
    | {
          type: "nullability";
          fieldId: Uuid;
          operator: "isnull" | "isnotnull";
      }
    | {
          /**
           * True when the signed-in user's template (role) is one of `values`.
           * Use inside CTA `inVisibility` to hide the button for those roles.
           */
          type: "userTemplate";
          fieldId: Uuid;
          operator: "in";
          values: Array<Uuid>;
      };

/**
 * Shared field-check group — reused by CTA `inVisibility` / `disability`,
 * FieldBase / FormField `*When`, and any other conditional flag.
 * - `logic: "and"` → every clause must match
 * - `logic: "or"` → any clause may match
 * - parent key `null` → static flag only (no dynamic condition)
 */
export type Conditions = {
    logic: "and" | "or";
    conditions: Array<Conditions | FilterItem>;
};

/**
 * Map of kind → entry. Lets registries stay keyed by UUID while props/ops
 * stay typed per kind (no `Record<string, unknown>`).
 */
type KindUnion<M extends Record<string, unknown>> = {
    [K in keyof M]: {kind: K} & M[K];
}[keyof M];

export enum DataSourceKind {
    Event = "dee3645e-335a-4189-b6ce-5919c92c15a8",
    EventWithEventContainer = "bd03db7e-3244-4e14-9805-8fe5559aa4bf",
    EventContainerWithEvent = "33399c00-5b5f-4efa-91ce-d0fc8b034d6a",
    AgentViewDayPlan = "0cadcf45-d653-4f84-91be-16bd4f8d3a4e",
    DuluxAgentViewDayPlan = "a9c36e42-7d5a-4b13-af48-8e2a5d1c3f64",
    /** Shared list source for every Tuffex catalogue page. The page id picks the rows. */
    TuffexProduct = "b8d4f2c3-5e69-4a01-ab72-9c1d2e3f4051",
}

type DataSourceByKind = {
    [DataSourceKind.Event]: {
        displayName: string;
        description: string | null;
    };
    [DataSourceKind.EventWithEventContainer]: {
        displayName: string;
        description: string | null;
    };
    [DataSourceKind.EventContainerWithEvent]: {
        displayName: string;
        description: string | null;
    };
    [DataSourceKind.AgentViewDayPlan]: {
        displayName: string;
        description: string | null;
    };
    [DataSourceKind.DuluxAgentViewDayPlan]: {
        displayName: string;
        description: string | null;
    };
    [DataSourceKind.TuffexProduct]: {
        displayName: string;
        description: string | null;
    };
};

export type DataSource = KindUnion<DataSourceByKind>;

export enum ResolverKind {
    OrganizationalUnit = "d2d8f0ba-d526-49fe-9d3d-302483978029",
    Priority = "07b1036a-a18f-4678-ae9a-1a0481213f5b",
    Disposition = "f52557c1-46ae-4f29-847f-f7f2b438b52a",
    User = "8f140542-eee4-4c0d-a574-ed6ded350048",
}

type ResolverByKind = {
    [ResolverKind.OrganizationalUnit]: {
        displayName: string;
        description: string | null;
        /** Inputs the OU label/path function needs. */
        props: {
            /** Field holding the OU UUID. */
            organizationalUnitFieldId: Uuid;
            /** Optional template field for OU type display. */
            organizationalUnitTemplateFieldId: Uuid | null;
        };
    };
    [ResolverKind.Priority]: {
        displayName: string;
        description: string | null;
        props: {
            priorityFieldId: Uuid;
            /** Option UUID → label/colors; `null` → use field `options` / columnMapping. */
            valueOptions: Record<
                string,
                {
                    label: string;
                    backgroundColorToken: string | null;
                    textColorToken: string | null;
                }
            > | null;
        };
    };
    [ResolverKind.Disposition]: {
        displayName: string;
        description: string | null;
        props: {
            /** L1→Ln disposition field ids joined for path display. */
            dispositionFieldIds: Array<Uuid>;
            separator: string;
        };
    };
    [ResolverKind.User]: {
        displayName: string;
        description: string | null;
        props: {
            /** Field holding the workspace or platform user UUID. */
            userFieldId: Uuid;
            /** Optional user template used to label the role (e.g. ISD). */
            userTemplateId: Uuid | null;
        };
    };
};

export type Resolver = KindUnion<ResolverByKind>;

export type IconRegistryEntry = {
    displayName: string;
    name: string;
};

type FixedColumnName =
    | "id"
    | "journeyId"
    | "templateId"
    | "createdAt"
    | "updatedAt"
    | "createdBy"
    | "updatedBy"
    | "status";

export type FieldFormatter = "text" | "date" | "dateTime" | "select" | "user" | "role" | "number" | "disposition";

/** Path segment index for `formatter: "disposition"` column mapping (L1–L4). */
export type DispositionMappingLevel = 1 | 2 | 3 | 4;

export type FieldOption = {
    label: string;
    backgroundColorToken: string | null;
    textColorToken: string | null;
    borderColorToken: string | null;
};

export type FieldOptionSource = {
    type: "userDefinedObject";
    userDefinedObjectTemplateId: Uuid;
};

type SelectFieldOptions =
    | {
          options: Record<string, FieldOption> | null;
          optionSource?: never;
      }
    | {
          options?: never;
          optionSource: FieldOptionSource;
      };

type UdoFieldOptions = {
    options?: never;
    optionSource: FieldOptionSource;
};

/** Bare entity kind used at runtime (row read / API entity). */
export type ColumnMappingEntityKind = "event" | "eventContainer";

/**
 * Template-qualified entity ref on {@link ColumnMappingEntry}:
 * `event.<eventTemplateId>` or `eventContainer.<eventContainerTemplateId>`.
 */
export type ColumnMappingEntityRef = `${ColumnMappingEntityKind}.${Uuid}`;

export type ColumnMappingEntry =
    | ({
          type: "additionalColumn";
          columnId: Uuid;
          entity: ColumnMappingEntityRef;
          displayName: string;
          formatter: "select";
      } & SelectFieldOptions)
    | ({
          type: "additionalColumn";
          columnId: Uuid;
          entity: ColumnMappingEntityRef;
          displayName: string;
          formatter: "disposition";
          level: DispositionMappingLevel;
      } & UdoFieldOptions)
    | {
          type: "additionalColumn";
          columnId: Uuid;
          entity: ColumnMappingEntityRef;
          displayName: string;
          formatter: Exclude<FieldFormatter, "select" | "disposition">;
      }
    | {
          type: "fixedColumn";
          columnName: FixedColumnName;
          entity: ColumnMappingEntityRef;
          displayName: string;
          formatter: "select";
          options: Record<string, FieldOption> | null;
      }
    | {
          type: "fixedColumn";
          columnName: FixedColumnName;
          entity: ColumnMappingEntityRef;
          displayName: string;
          formatter: "text" | "dateTime" | "user" | "role";
      };

export type ColumnMapping = Record<Uuid, ColumnMappingEntry>;

export function isDispositionColumnMappingEntry(
    entry: ColumnMappingEntry,
): entry is Extract<ColumnMappingEntry, {formatter: "disposition"}> {
    return entry.formatter === "disposition";
}

/** Backend column id or fixed column name where the leaf disposition UUID is stored. */
export function resolveDispositionColumnMappingSource(entry: ColumnMappingEntry): string {
    if (entry.type === "additionalColumn") {
        return String(entry.columnId);
    }
    return entry.columnName;
}

export function resolveColumnMappingDispositionLevel(
    entry: ColumnMappingEntry,
): DispositionMappingLevel | null {
    if (isDispositionColumnMappingEntry(entry) !== true) {
        return null;
    }
    if (entry.level !== 1 && entry.level !== 2 && entry.level !== 3 && entry.level !== 4) {
        return null;
    }
    return entry.level;
}

export function parseColumnMappingEntity(
    entity: string,
): {kind: ColumnMappingEntityKind; templateId: Uuid} | null {
    if (entity.startsWith("eventContainer.")) {
        const templateId = entity.slice("eventContainer.".length);
        if (templateId.length === 0) {
            return null;
        }
        return {kind: "eventContainer", templateId: templateId as Uuid};
    }
    if (entity.startsWith("event.")) {
        const templateId = entity.slice("event.".length);
        if (templateId.length === 0) {
            return null;
        }
        return {kind: "event", templateId: templateId as Uuid};
    }
    return null;
}

export function columnMappingEntityKind(entity: string): ColumnMappingEntityKind | null {
    return parseColumnMappingEntity(entity)?.kind ?? null;
}

export type CtaTableAction =
    | "profile"
    | "disposition"
    | "edit"
    | "view"
    | "create"
    | "logCall"
    | "notes"
    | "scheduleCall"
    | "archive"
    | "cancelCall";

export type CtaPlacement =
    | "toolbar"
    | "toolbarMore"
    | "primary"
    | "more"
    | "calendarEmptyState"
    | "mobileBottomCreate"
    | "detail"
    | "formFooter"
    | "both";

export type FormQueryParam = "eventId" | "eventContainerId" | "eventContainerTemplateId" | "journeyId" | "workspaceId" | "productId" | "quotationId";

export type CtaConfirm = {
    title: string;
    body: string;
    confirmLabel: string;
    appearance: "danger" | "warning" | null;
};

export type CtaAction =
    | {
          type: "openForm";
          formId: Uuid;
          presentation: "page" | "aside" | "drawer" | "modal" | "inline" | null;
          preferInlineModal: boolean;
          queryParams: Array<FormQueryParam> | null;
      }
      | {
          type: "openPage";
          pageId: Uuid;
          /** `aside` / `drawer` → right panel; `page` → full route. Prefer {@link openComponent} for Lead profile. */
          presentation: "page" | "aside" | "drawer" | "modal" | null;
          preferInlineModal: boolean;
          queryParams: Array<FormQueryParam> | null;
      }
    | {
          /**
           * Open a non-form UI component (e.g. Lead profile aside).
           * Forms that open as drawers stay on {@link openForm} + `presentation: "drawer"` — do not move them here.
           */
          type: "openComponent";
          componentId: Uuid;
          queryParams: Array<FormQueryParam> | null;
      }
    | {
          type: "command";
          commandId: string;
          /**
           * Chrome for confirm/command UIs. Sole driver when set (no form layout fallback).
           * Omit / `null` → host default (typically modal for confirm commands, drawer for notes).
           */
          presentation?: "page" | "aside" | "drawer" | "modal" | null;
          /** Confirm dialog copy for destructive/confirm commands (e.g. archive). Omit / `null` → no confirm shell. */
          confirm?: CtaConfirm | null;
      }
    | {
          type: "dropdown";
          ctaIds: Array<Uuid>;
      };

export type ArgusClickLocation = "sidebar" | "bottombar" | "header";
export type ArgusClickLevel = "primary" | "secondary" | "tertiary";
export type ArgusClickControlType = "navigate" | "dropdown" | "icon";

/**
 * Optional Argus autocapture metadata for a clickable scaffold/CTA control.
 * Stamped onto the DOM as `data-analytics-event` / `data-analytics-props`.
 * `event` is the control label, or the header item type when there is no label.
 */
export type ArgusClickConfig = {
    event: string;
    props?: {
        location: ArgusClickLocation;
        level: ArgusClickLevel;
        parent: string;
        type: ArgusClickControlType;
    };
};

/**
 * CTA registry entry.
 * - `inVisibility: null` → always shown when mounted in a slot.
 * - `inVisibility: Conditions` → hidden when the condition group matches.
 * - `disability: null` → never disabled by config.
 * - `disability: Conditions` → disabled when the condition group matches.
 */
export type Cta = {
    id: Uuid;
    label: string;
    description: string | null;
    appearance: "primary" | "default" | "subtle" | "danger" | "link" | null;
    iconColorToken: string | null;
    textColorToken: string | null;
    icon:
        | "phone"
        | "edit"
        | "view"
        | "more"
        | "archive"
        | "notes"
        | "disposition"
        | "profile"
        | "create"
        | "schedule"
        | "logCall"
        | "cancel"
        | "sales"
        | "inviteUser"
        | "calendar"
        | "transfer"
        | null;
    placements: Array<CtaPlacement> | null;
    tableAction: CtaTableAction | null;
    inVisibility: Conditions | null;
    disability: Conditions | null;
    action: CtaAction;
    argus?: ArgusClickConfig | null;
};

export enum PageType {
    EventPage = "28dd0987-0b82-4eb5-a3c9-ae890a791e8f",
    EventContainerWithEvents = "ac6e7473-a13f-4a87-8d6b-214a0ed92b10",
    /**
     * Tuffex product catalogue list. Same field / view / filter / CTA contract as
     * {@link EventContainerWithEvents}; rows come from the product catalogue.
     */
    TuffexProduct = "a7c3e1b2-4d58-4f90-9a61-8b0c1d2e3f40",
    TuffexProductCategory = "a8d4e2c1-5f69-4b01-8c72-1d2e3f405163",
    TuffexProductType = "b9e5f3d2-607a-4c12-9d83-2e3f40516374",
    TuffexProductBrand = "c0f604e3-718b-4d23-ae94-3f4051637485",
    TuffexProductReviewQueue = "d10715f4-829c-4e34-bf05-405163748596",
    EventWithEventContainer = "313e9457-03e8-497a-8f38-db1e13f29e50",
    DashBoard = "5720f132-653b-4252-9d3e-46258b68620f",
    Leaderboard = "7e4a1c2b-9f83-4d6a-b5e1-2c8d0f4a6b91",
    /**
     * Config + analytical-engine dataset leaderboard (no saved boards / ottopilot_leaderboards).
     */
    FrontendLeaderboard = "c3e8a1b2-4d5f-4789-a012-9b8c7d6e5f40",
    AgentView = "2fe9d4b8-f0eb-45f0-a56f-4921b3797ca8",
    AgentHomePage = "62fe4b59-0e98-44df-a8f2-95551e88b4d4",
    DuluxAgentHomePage = "e7a14c20-5b38-4f91-8d26-6c0e3b9a1d42",
    DuluxMyDayPlanPage = "f8b25d31-6c49-4a02-9e37-7d1f4c0b2e53",
    /**
     * Empty branding home — workspace display name + optional attribution line.
     * Prefer {@link PageType.DuluxDashboard} when the workspace home is the Dulux dashboard.
     */
    WorkspaceBrandingHome = "8c9d0e1f-2a3b-4c5d-9e0f-1a2b3c4d5e6f",
    DispositionTracker = "4b59e0d7-b0ad-4c87-a35c-ed1e34374aee",
    FormPage = "d1245784-dbf8-4fde-aada-45b08ee7ddcf",
    /** Full-page journey / lead profile. */
    JourneyProfile = "7a8b9c0d-1e2f-4a5b-8c9d-0e1f2a3b4c5d",
    /**
     * Workspace hosted shells (not list/home/dashboard config pages).
     * Page `id` is `/$pageKey`; type selects `hostedPages/*` RouteComponent.
     */
    UserInvitation = "8b9c0d1e-2f3a-4b5c-9d0e-1f2a3b4c5d6e",
    Users = "9c0d1e2f-3a4b-5c6d-0e1f-2a3b4c5d6e7f",
    UserTemplates = "0d1e2f3a-4b5c-6d7e-1f2a-3b4c5d6e7f80",
    AccountSettings = "1e2f3a4b-5c6d-7e8f-2a3b-4c5d6e7f8091",
    OrganizationalUnits = "2f3a4b5c-6d7e-8f90-3a4b-5c6d7e8f9012",
    OrganizationalUnitTemplates = "3a4b5c6d-7e8f-9012-4b5c-6d7e8f901223",
    Notifications = "4b5c6d7e-8f90-1234-5c6d-7e8f90123434",
    /** Workspace tickets list (hosted copy of the tickets page). */
    Tickets = "7c3a9e14-2b68-4d5f-a1e0-6f8b2c4d9a73",
    Billing = "5c6d7e8f-9012-3456-6d7e-8f9012344556",
    Profile = "6d7e8f90-1234-5678-7e8f-901234566778",
    /** Data Connections catalog. */
    DataConnections = "7e8f9012-3456-7890-8f90-1234567890ab",
    /** Data-source instances owned by this workspace. */
    MyDataConnections = "8f901234-4567-8901-9012-345678901abc",
    /** Single data source (`?dataSourceId=`). */
    DataSourceDetail = "90123456-5678-9012-a123-456789012bcd",
    /** Data-source connection onboarding (`?onboardingJourneyId=`). */
    ConnectDataSources = "a1234567-6789-0123-b234-567890123cde",
    Products = "b2345678-7890-1234-c345-678901234def",
    ProductInventories = "c3456789-8901-2345-d456-789012345ef0",
    ProductCatalogueTemplates = "d4567890-9012-3456-e567-890123456f01",
    RetailLocations = "6c41a0dd-ed34-4e83-abf3-a5a0b29cacfd",
    RetailLocationMicroStores = "1c1afbcc-b204-4584-b9b5-508c5ec58851",
    RetailLocationMicroStoreDefaultPages = "61ca0a6c-c1ca-41ee-81c4-e423764891de",
    /** Store Configuration — micro-store pages for a selected store. */
    StoreConfiguration = "c7e4a91b-2d58-4f3a-9b6e-8a1c0d4f2753",
    /** Store / default-store page editor (`?retailLocationId=` or `?defaultStore=true`). Never renders the list. */
    StoreConfigurationEdit = "c8f5b02c-3d69-4a1b-9c7e-8a0d1f2b3c4e",
    /** Blue Stores invite list (hosted copy of `/w/$workspaceId/bluestores`). */
    BlueStores = "e5f6a7b8-c9d0-4123-8a45-678901234f01",
    /** Invite a Blue Store (hosted copy of `/w/$workspaceId/bluestores/invite`). */
    InviteBlueStores = "e5f6a7b8-c9d0-4123-8a45-678901234f02",
    /** Store-owner invite list. Always scoped to invites created by the signed-in user. */
    DuluxStoreOwnerUserInvites = "a9b8c7d6-5e4f-4a3b-8c2d-1e0f9a8b7c6d",
    /** Store-owner invite form for a store manager or store executive. */
    DuluxStoreOwnerUserInvite = "c4d8e2f1-6a3b-4c7d-8e9f-1a2b3c4d5e60",
    /** Dulux Blue Store quotation list. */
    DuluxBlueStoreQuotations = "c17ad010-9b2c-4399-aa38-dcf3d2630329",
    /** Dulux Blue Store quotation creation page. */
    DuluxBlueStoreQuotationCreate = "b66eb118-bad2-4288-86c9-9c6c80cd6625",
    /** Dulux Blue Store quotation detail page. */
    DuluxBlueStoreQuotationDetail = "442e5be5-7074-4512-9907-7c3f4d865137",
    /** Dulux Blue Store quotation revision page. */
    DuluxBlueStoreQuotationRevise = "a0a78ca5-4f28-4ca4-b191-dc5b6075f5dd",
    /** Dulux Blue Store custom quotation creation page. */
    DuluxBlueStoreCustomQuotationCreate = "edaa650e-5aec-4c87-aad3-3aa2f02c3578",
    /** Dulux Blue Store custom quotation detail page. */
    DuluxBlueStoreCustomQuotationDetail = "6e9be66e-2503-4ac7-9c12-997d90206056",
    /** Dulux Blue Store custom quotation revision page. */
    DuluxBlueStoreCustomQuotationRevise = "acc35564-43f6-42df-8646-f2762d286944",
    Reports = "f6789012-1234-5678-0789-012345678123",
    FavouriteReports = "0789abcd-2345-6789-1890-123456789234",
    Collections = "1890bcde-3456-7890-29a1-23456789a345",
    /** Collection detail deep-link (`?collectionId=`). */
    CollectionDetail = "4bc3def0-6789-0123-5cd4-56789abcd678",
    /** Report viewer deep-link (`?reportId=`). */
    ReportDetail = "29a1bcde-4567-8901-3ab2-3456789ab456",
    /** Report editor deep-link (`?reportId=`). */
    ReportEdit = "3ab2cdef-5678-9012-4bc3-456789abc567",
    /** Ottokart admin Leads list (hosted copy of ottokart/.../admin/leads). */
    OttokartLeads = "e0b855e4-4efe-4ee6-b021-cca9dab93fec",
    /** Ottokart admin Customers list (hosted copy of ottokart/.../admin/customers). */
    OttokartCustomers = "f1a2b3c4-d5e6-4f70-8a91-b2c3d4e5f607",
    /** Ottokart admin Products list (hosted copy of ottokart/.../admin/products). */
    OttokartProducts = "a3b4c5d6-e7f8-4091-8b2c-3d4e5f607182",
    /** Ottokart admin product review queue (hosted copy of ottokart/.../admin/products/review-queue). */
    OttokartProductReviewQueue = "b4c5d6e7-f809-4123-8c3d-4e5f60718293",
    /** Ottokart admin product categories (hosted copy of ottokart/.../admin/products/categories). */
    OttokartProductCategories = "c5d6e7f8-0912-4234-9d4e-5f60718293a4",
    /** Ottokart admin product types (hosted copy of ottokart/.../admin/products/types). */
    OttokartProductTypes = "d6e7f809-1234-4345-ae5f-60718293a4b5",
    /** Ottokart admin product brands (hosted copy of ottokart/.../admin/products/brands). */
    OttokartProductBrands = "e7f80912-2345-4456-bf60-718293a4b5c6",
    /** Ottokart admin Product Collections list (hosted copy of ottokart/.../admin/collections). */
    OttokartCollections = "f8091234-3456-4567-c071-8293a4b5c6d7",
    /** Ottokart admin Machines list (hosted copy of ottokart/.../admin/machines). */
    OttokartMachines = "091a2b3c-4567-4678-8d82-93a4b5c6d7e8",
    /** Ottokart admin machine types (hosted copy of ottokart/.../admin/machines/types). */
    OttokartMachineTypes = "1a2b3c4d-5678-4789-9e93-a4b5c6d7e8f9",
    /** Ottokart admin machine review queue (hosted copy of ottokart/.../admin/machines/review-queue). */
    OttokartMachineReviewQueue = "2b3c4d5e-6789-489a-af04-b5c6d7e8f90a",
    /** Ottokart admin OEM Parts list (hosted copy of ottokart/.../admin/oem-parts). */
    OttokartOemParts = "3c4d5e6f-789a-49ab-8051-c6d7e8f90a1b",
    /** Ottokart admin Manufacturers list (hosted copy of ottokart/.../admin/manufacturers). */
    OttokartManufacturers = "4d5e6f70-89ab-4abc-9162-d7e8f90a1b2c",
    /** Ottokart admin Quotations list (hosted copy of ottokart/.../admin/quotations). */
    OttokartQuotations = "5e6f7081-9abc-4bcd-a273-e8f90a1b2c3d",
    /** Ottokart admin Advanced quotations list (hosted copy of ottokart/.../admin/advanced-quotations). */
    OttokartAdvancedQuotations = "6f708192-abcd-4cde-8384-f90a1b2c3d4e",
    /** Ottokart admin Inventory list (hosted copy of ottokart/.../admin/inventory). */
    OttokartInventory = "708192a3-bcde-4def-9495-0a1b2c3d4e5f",
    /** Ottokart quotation version list (clicking a quotation row). */
    OttokartQuotationDetail = "b1c2d3e4-f5a6-4789-8bcd-ef1234567890",
    /** Ottokart view quotation detail page. */
    OttokartViewQuotation = "f5a6b7c8-d9e0-4123-8cde-234567890124",
    /** Ottokart revise quotation page. */
    OttokartReviseQuotation = "c2d3e4f5-a6b7-4890-9cde-f01234567891",
    /** Ottokart create quotation wizard. */
    OttokartCreateQuotation = "d3e4f5a6-b7c8-4901-acde-012345678902",
    /** Ottokart duplicate quotation wizard. */
    OttokartDuplicateQuotation = "e4f5a6b7-c8d9-4012-bcde-123456789013",
    /** Ottokart advanced quotation version list. */
    OttokartAdvancedQuotationDetail = "a6b7c8d9-e0f1-4234-9def-345678901235",
    /** Ottokart view advanced quotation detail page. */
    OttokartAdvancedViewQuotation = "e0f1a2b3-c4d5-4678-9123-789012345679",
    /** Ottokart revise advanced quotation page. */
    OttokartAdvancedReviseQuotation = "b7c8d9e0-f1a2-4345-adef-456789012346",
    /** Ottokart create advanced quotation wizard. */
    OttokartAdvancedCreateQuotation = "c8d9e0f1-a2b3-4456-bdef-567890123457",
    /** Ottokart duplicate advanced quotation wizard. */
    OttokartAdvancedDuplicateQuotation = "d9e0f1a2-b3c4-4567-8012-678901234568",
    /** Ottokart create product page. */
    OttokartCreateProduct = "f1e2d3c4-b5a6-4789-8901-234567890abc",
    /** Ottokart edit product page. */
    OttokartEditProduct = "a1b2c3d4-e5f6-4789-9012-345678901bcd",
    /** Ottokart view product page. */
    OttokartViewProduct = "b2c3d4e5-f6a7-4890-a123-456789012cde",
    /** Ottokart standalone edit-variant page (hosted copy of ottokart/.../products/$productId/$variantId). */
    OttokartEditVariant = "e5f6a7b8-c9d0-4123-8a45-67890135fb12",
    /** Ottokart admin home dashboard (hosted copy of ottokart/.../admin). */
    OttokartHome = "c3d4e5f6-a7b8-4901-b234-56789013def0",
    /** Ottokart quotation analytics dashboard (config page hosted shell). */
    OttokartQuotationDashboard = "7f0d7d31-8f6e-4a21-9a45-2c9f0b5e7d11",
    /** Ottokart inventory analytics dashboard (config page hosted shell). */
    OttokartInventoryDashboard = "8e1e8e42-9f7f-4b32-a956-3da01c6f8e22",
    /** Workspace Media Library (config-scaffold hosted page). */
    MediaLibrary = "d4e5f6a7-b8c9-4012-c345-678901234ef0",
    /** Workspace Blogs list (create/edit/preview are handled within this hosted page). */
    Blogs = "e4f5a6b7-c8d9-4012-b345-678901234ef0",
    /** Dulux localized city landing-page CMS. */
    DuluxLocationPages = "6b546cdf-cc75-4d46-92d0-8ec3c9a79f5a",
    StableLinks = "bb6c29c2-a859-47f5-a544-6c80459c8123",
    /** Intellsys ads dashboard (hosted copy of `/w/$workspaceId/dashboard`). */
    IntellsysDashboard = "6e7f8091-2345-4a6b-8c7d-8e9f0a1b2c3d",
    /** Amazon ads dashboard (hosted copy of `/w/$workspaceId/amazon-dashboard`). */
    AmazonDashboard = "amazon-dashboard",
    /** Google ads dashboard (hosted copy of `/w/$workspaceId/google-dashboard`). */
    GoogleDashboard = "google-dashboard",
    /** Meta ads dashboard (hosted copy of `/w/$workspaceId/meta-dashboard`). */
    MetaDashboard = "meta-dashboard",
    /** Dulux performance dashboard (hosted copy of `/w/$workspaceId/dulux-dashboard`). */
    DuluxDashboard = "dulux-dashboard",
    AmaronSourceWiseLeadFunnel = "source-wise-lead-funnel",
    AmaronCampaignOverview = "amaron-campaign-overview",
    AmaronBcgGoogleDashboard = "bcg-google-dashboard",
    AmaronBcgMetaDashboard = "bcg-meta-dashboard",
    AmaronBusinessSummary = "874a84d9-3eb5-477d-bef5-d30889f312aa",
    /** Individual/bulk transfer form shell (`?journeyId=`). */
    TransferForm = "81a2b3c4-d5e6-4f70-89a1-b2c3d4e5f607",
    /** Transfer preview shell (`?transferSessionId=`). */
    TransferPreview = "92b3c4d5-e6f7-4081-9ab2-c3d4e5f60718",
}

/**
 * Page field catalog entry — UI / validation only.
 * Column binding (`type` / `entity` / `columnId` / `columnName`) lives in {@link ColumnMapping}.
 */
export type FieldBase = {
    id: Uuid;
    label: string;
    resolverId: Uuid | null;
    defaultValue: string | boolean | number | null;
    isVisible: boolean;
    isReadOnly: boolean;
    isRequired: boolean;
    /**
     * null → use static `isRequired` / `isReadOnly` / `isVisible` only.
     * Conditions → apply that flag when the group matches.
     */
    requiredWhen: Conditions | null;
    readonlyWhen: Conditions | null;
    visibleWhen: Conditions | null;
    errorMessage: string | null;
    validation: unknown;
    /**
     * Shown when the stored value is null or empty.
     * Omitted pages keep the existing blank / "-" display.
     */
    emptyLabel?: string | null;
};

export type FieldType =
    | (FieldBase & {
          formatter: "select";
      } & SelectFieldOptions)
    | (FieldBase & {
          formatter: "disposition";
      } & UdoFieldOptions)
    | (FieldBase & {
          formatter: Exclude<FieldFormatter, "select" | "disposition">;
      });

/** Shared inline CTA slots (table row / list card / kanban card / focus footer). */
export type InlineCtaSlots = {
    primaryCtaIds: Array<Uuid>;
    moreCtaIds: Array<Uuid>;
};

/** Page chrome toolbar Create / ⋮ slots. */
export type ViewEngineToolbarCtaSlots = {
    toolbarCtaIds: Array<Uuid>;
    toolbarMoreCtaIds: Array<Uuid>;
};

export type CardDetailCell =
    | {
          type: "SingleColumn";
          fieldId: Uuid;
          label: string;
          iconId: Uuid | null;
      }
    | {
          type: "MultipleColumn";
          fieldIds: Array<Uuid>;
          separator: string;
          label: string;
          iconId: Uuid | null;
      };

export type CardDetailGrid = Array<Array<CardDetailCell>>;

export type CardProgress =
    | {presentation: "funnel"; stageFieldIds: Array<Uuid>}
    | {presentation: "ring"; fieldId: Uuid; labelTemplate: string};

export type CardEventsBar = {
    label: string;
    iconId: Uuid | null;
    isCollapsible: boolean;
    isCollapsed: boolean;
    items: Array<{fieldId: Uuid; label: string}>;
};

export type CardActionBar =
    | {
          dueMode: "queueTat";
          actionLabel: string;
          phoneFieldId: Uuid | null;
          dueFieldId: null;
      }
    | {
          dueMode: "field";
          actionLabel: string;
          phoneFieldId: Uuid | null;
          dueFieldId: Uuid;
      };

type CardMetaItemBase = {
    fieldId: Uuid;
    iconId: Uuid | null;
    label: string | null;
};

/** Plain meta text (+ optional icon). */
export type CardMetaSimpleItem = CardMetaItemBase & {
    presentation: "simple";
};

/**
 * Pill / lozenge meta. Colors are fallbacks when the field option has none;
 * select fields prefer option colors for the row value.
 * Each color accepts a design-token name (`color.text.accent.blue`) or a CSS color (`#RRGGBB`, `rgb(...)`, `var(...)`).
 */
export type CardMetaPillItem = CardMetaItemBase & {
    presentation: "pill";
    backgroundColor: string | null;
    textColor: string | null;
    iconColor: string | null;
};

export type CardMetaItem = CardMetaSimpleItem | CardMetaPillItem;

/**
 * List / kanban card chrome. CTA visibility / disability live on {@link Cta}
 * (`inVisibility` / `disability`) — not on the card.
 */
export type CardLayout = InlineCtaSlots & {
    title: {fieldIds: Array<Uuid>; separator: string};
    subtitle: {fieldIds: Array<Uuid>; separator: string} | null;
    status: {
        fieldId: Uuid;
        presentation: "pill" | "text";
        label: string | null;
    } | null;
    progress: CardProgress | null;
    dispositionBand: {fieldIds: Array<Uuid>; separator: string} | null;
    actionBar: CardActionBar | null;
    meta: Array<CardMetaItem>;
    details: CardDetailGrid;
    actionSlot: "header" | "footer";
    showDividerBeforeDetails: boolean;
    showAssignedOu: boolean | null;
    showDispositionChip: boolean | null;
    eventsBar: CardEventsBar | null;
    showPhoneDial: boolean | null;
    /**
     * When true, the kanban day-plan footer includes `moreCtaIds`.
     * Omitted or false keeps the primary actions only.
     */
    showOverflowCtas?: boolean;
};

export type ViewType = "table" | "list" | "kanban" | "calendar" | "focus";

type FocusDetailsSectionBase = {
    id: Uuid;
    title: string;
    iconId: Uuid | null;
    iconColorToken: string | null;
    iconBackgroundColorToken: string | null;
    initialIsOpen: boolean;
};

export type FocusDetailsSection =
    | (FocusDetailsSectionBase & {
          kind: "sales-assistance";
      })
    | (FocusDetailsSectionBase & {
          kind: "fields";
          fields: Array<{
              fieldId: Uuid;
              label: string | null;
              actions: {copy: boolean; whatsapp: boolean} | null;
          }>;
      })
    | (FocusDetailsSectionBase & {
          kind: "notes";
          previewLimit: number;
          showViewAll: boolean;
          eventContainerSourceLabel: string | null;
      });

export type FocusViewConfig = InlineCtaSlots & {
    type: "focus";
    details: {
        columns: number;
        sections: Array<FocusDetailsSection>;
    };
    categoryFieldId: Uuid;
    defaultCategoryId: Uuid;
    categories: Array<{
        id: Uuid;
        label: string;
        apiValue: string;
        searchParam: string;
        values: Array<Uuid>;
        backgroundColorToken: string;
        textColorToken: string;
    }>;
    header: {
        titleFieldIds: Array<Uuid>;
        phoneFieldId: Uuid;
        sourceFieldId: Uuid;
        categoryFieldId: Uuid;
        /**
         * Pill value on the focus card. Omitted → `categoryFieldId` (the chip filter field).
         */
        categoryDisplayFieldId?: Uuid | null;
        /** Pill caption. Omitted → "Category". */
        categoryDisplayLabel?: string | null;
        dispositionFieldIds: Array<Uuid>;
        dueFieldId: Uuid;
        progress: {presentation: "funnel"; stageFieldIds: Array<Uuid>};
    };
    footerAppearance: {
        iconColorToken: string | null;
        textColorToken: string | null;
    } | null;
};

export type TableViewConfig = InlineCtaSlots & {
    type: "table";
    possibleColumns: Array<Uuid>;
    defaultVisibleColumns: Array<Uuid>;
};

export type ListViewConfig = {
    type: "list";
    cardLayout: CardLayout;
};

export type KanbanViewConfig = {
    type: "kanban";
    statusField: Uuid;
    statusGroups: Array<{
        id: Uuid;
        label: string;
        values: Array<Uuid>;
        backgroundColorToken: string | null;
        textColorToken: string | null;
        /**
         * Kanban blue-box timing caption for rows in this group (for example "Due in").
         * Omitted → the card keeps `actionBar.dueFieldId`.
         */
        timingLabel?: string | null;
    }>;
    cardLayout: CardLayout;
    layout: {
        columnMinWidthPx: number | null;
        columnGapPx: number | null;
        mobileCardsGridTemplateColumns: string | null;
        hideColumnPin: boolean | null;
    } | null;
    columnHeaderStyle: {
        backgroundColorToken: string | null;
        textColorToken: string | null;
        headerBorderRadius: string | null;
        headerBorderColor: string | null;
        headerBorderWidth: string | null;
    } | null;
};

export type CalendarViewConfig = {
    type: "calendar";
    startField: Uuid;
    eventTypeField: Uuid;
    titleFields: Array<Uuid>;
    legendItems: Array<{type: Uuid; label: string}>;
    eventStatusColors: Record<
        Uuid,
        {
            backgroundColorToken: string;
            borderColorToken: string;
            textColorToken: string;
        }
    >;
    allDayStyle: "fixedGreen";
};

export type PageView =
    | TableViewConfig
    | ListViewConfig
    | KanbanViewConfig
    | CalendarViewConfig
    | FocusViewConfig;

export type PageViewEngine = ViewEngineToolbarCtaSlots & {
    defaultDesktopView: ViewType;
    defaultMobileView: ViewType;
    searchFields: Array<Uuid>;
    journeyScope: {
        title: string;
        statusColumnId: Uuid;
        defaultApplied: Uuid;
        options: Array<{
            id: Uuid;
            displayName: string;
            values: Array<Uuid> | null;
        }>;
    } | null;
    /**
     * "All" scope control (Assigned to me / Created by me).
     * Same contract as production {@link ScopeFilterConfig}:
     * - `filterKey` is a page field id, column id, or system key (`createdBy`)
     * - `type: "User"` resolves to the signed-in workspace user when applied
     */
    scopeFilter: {
        drawerTitle: string;
        selectionMode: "single" | "multi";
        options: Array<{
            id: Uuid;
            filterKey: string;
            label: string;
            type: "User" | "value";
            filterType: "equals" | "in";
            defaultValue?: string;
        }>;
    } | null;
    inQueueAssignmentFilter: {
        leadOwnerFieldId: Uuid;
    } | null;
    views: Array<PageView>;
};

/**
 * Shared chrome for list-style pages ({@link EventPage}, {@link EventWithEventContainerPage},
 * {@link EventContainerWithEventsPage}, {@link AgentViewPage}).
 * Type-specific params live on each page arm — not on a parallel "view" type.
 * Transport ops come from `dataSources[dataSourceId]` (path/schema only).
 */
type ListPageBase = {
    /** Optional workspace role allow-list. Empty means no roles can open the page; platform admin is not a bypass. */
    allowedUserTemplateIds?: Array<Uuid>;
    /** Display-only page: no configured CTAs, mutation panels, selection/transfer or export actions. */
    readOnly?: boolean;
    id: Uuid;
    title: string;
    /** When true, save this page's filters/sort/column layout in localStorage. */
    persistInLocalStorage: boolean;
    archivedPageId: Uuid | null;
    parentActivePageId: Uuid | null;
    /** Sidebar parent when this page is not itself a nav link. */
    parentPageId?: Uuid | null;
    /**
     * Event-container template whose disposition UDO is this page's L1–L4 catalog.
     * Omitted → first config entry with `provideDisposition: true`.
     */
    dispositionProviderEventContainerTemplateId?: Uuid;
    /** Lookup in workspace `dataSources` — use `.operations` only. */
    dataSourceId: Uuid;
    /** Initial sort — page-owned (not on DataSource). */
    defaultSort: Array<SortItem>;
    filters: {
        defaultApplied: Array<FilterItem>;
        /** Field owned by the date-range picker; omitted/null keeps Created At. */
        dateRangeFieldId?: Uuid | null;
        visibleFieldIds: Array<Uuid>;
        userCanClear: boolean;
        /** When `false`, skip the auto last-30-days range on dateRangeFieldId (Created At by default). `null` → host default. */
        defaultCreatedAtMonthFilter: boolean | null;
        /** When `true`, hide the date-range pill. `null` → host default. Legacy property name retained. */
        hideCreatedAtDateRangeFilter: boolean | null;
    };
    /** Locked filters (e.g. status ≠ archived). Not clearable. */
    mandatoryFilters: Array<FilterItem> | null;
    fields: Array<FieldType>;
    viewEngine: PageViewEngine;
};

export type AgentViewProfileTabId = "details" | "activity" | "notes";

export type AgentViewProfileTab =
    | AgentViewProfileTabId
    | {id: AgentViewProfileTabId; label: string | null};

export type AgentViewProfilePanel = {
    viewFormId: Uuid;
    editFormId: Uuid | null;
    headerTitle: string | null;
    tabs: Array<AgentViewProfileTab> | null;
    eventContainerSourceLabel: string | null;
    footerToolbarCtaIds: Array<Uuid>;
    footerMoreCtaIds: Array<Uuid>;
    footerAppearance: {
        iconColorToken: string | null;
        textColorToken: string | null;
    } | null;
};

/** Tabs for full-page journey profile (matches production Lead profile). */
export type JourneyProfileTabId = "sales-assistance" | "events" | "details" | "activity" | "notes";

export type JourneyProfileTab =
    | JourneyProfileTabId
    | {id: JourneyProfileTabId; label: string | null};

export type JourneyProfilePanel = {
    viewFormId: Uuid;
    editFormId: Uuid | null;
    headerTitle: string | null;
    tabs: Array<JourneyProfileTab> | null;
    eventContainerSourceLabel: string | null;
    footerToolbarCtaIds: Array<Uuid>;
    footerMoreCtaIds: Array<Uuid>;
    footerAppearance: {
        iconColorToken: string | null;
        textColorToken: string | null;
    } | null;
};

export type AgentHomeBoxSelectAction =
    | {type: "dayPlanCategory"; filter: FilterItem}
    | {type: "navigatePage"; pageId: Uuid; filters: Array<FilterItem> | null; search: Record<string, string> | null}
    | null;

export type DispositionKanbanColumnConfig = {
    /** API / board column slug (e.g. `new-lead`). */
    slug: string;
    /** Column header label. */
    label: string;
};

export type AgentHomeDispositionLevelOption = {
    level: 1 | 2 | 3 | 4;
    label: string;
    /** Kanban columns for this level — order is display order. */
    columns: Array<DispositionKanbanColumnConfig>;
};

export type AgentHomeDispositionLevelConfig = {
    title: string;
    defaultLevel: 1 | 2 | 3 | 4;
    allowChange: boolean;
    possibleLevels: Array<AgentHomeDispositionLevelOption>;
};

/** Active / inactive lead-status ids for home disposition journey-scope filter. */
export type AgentHomeDispositionJourneyScopeConfig = {
    activeStatusIds: Array<Uuid>;
    inactiveStatusIds: Array<Uuid>;
};

export type AgentHomeTodayWorkCardConfig = {
    id: Uuid;
    /** Identifier returned by the configured stats data source. */
    dataKey: string;
    displayName: string;
    infoText: string | null;
    onSelect: AgentHomeBoxSelectAction;
};

export type AgentHomeYourQueueCategoryConfig = {
    id: Uuid;
    label: string;
    variant: "opportunity" | "emergency" | "atRisk" | "hot" | "warm" | "cold";
    filter: FilterItem;
};

export type AgentHomeDispositionCardConfig = {
    primaryCtaIds: Array<Uuid>;
    showPhoneDial: boolean;
    /**
     * Frontend field id (via {@link ColumnMapping}) for tel: dial when
     * {@link showPhoneDial} is true — same idea as disposition `levelFieldIds`.
     */
    phoneFieldId: Uuid | null;
    /** Disposition form opened by the Update CTA. */
    disposeFormId: Uuid | null;
    /** Event template id for the dispose modal event stub. */
    eventTemplateId: Uuid | null;
};

export type AgentHomeEngagementMetricConfig = {
    id: Uuid;
    label: string;
    filter: FilterItem;
};

export type AgentHomeEngagementGroupConfig = {
    id: Uuid;
    label: string;
    metrics: Array<AgentHomeEngagementMetricConfig>;
};

export type PerformanceOverviewCardId =
    | "sla_compliance"
    | "follow_up_adherence"
    | "first_response_time"
    | "at_risk_leads"
    | "connection_rate"
    | "queue_completion";

export type AgentHomePerformanceCardConfig = {
    id: PerformanceOverviewCardId;
    label: string;
    formula: string;
};

export type FocusDetailsFieldsSection = Extract<FocusDetailsSection, {kind: "fields"}>;
export type FocusDetailsNotesSection = Extract<FocusDetailsSection, {kind: "notes"}>;
export type FocusDetailsConfig = FocusViewConfig["details"];

export type AgentHomeSection =
    | {
          type: "title";
          description: string | null;
      }
    | {
          type: "todayWork";
          titleToday: string;
          titleHistorical: string;
          cards: Array<AgentHomeTodayWorkCardConfig>;
      }
    | {
          type: "yourQueue";
          previewLimit: number;
          hideListWhenHistorical: boolean;
          titleToday: string | null;
          titleHistorical: string | null;
          categories: Array<AgentHomeYourQueueCategoryConfig>;
          defaultCategoryId: Uuid;
      }
    | {
          type: "dispositionBasedView";
          hideWhenHistorical: boolean;
          title: string | null;
          /** Event-container template the kanban rows belong to. */
          eventContainerTemplateId: Uuid;
          /** Journey scope status ids; null disables scope filtering. */
          journeyScope: AgentHomeDispositionJourneyScopeConfig | null;
          dispositionLevel: AgentHomeDispositionLevelConfig;
          card: AgentHomeDispositionCardConfig;
      }
    | {
          type: "engagementHealth";
          title: string | null;
          groups: Array<AgentHomeEngagementGroupConfig>;
      }
    | {
          type: "performanceOverview";
          title: string | null;
          cards: Array<AgentHomePerformanceCardConfig>;
      };

export type AgentHomeSectionConfig = AgentHomeSection;

// ---------------------------------------------------------------------------
// Page variants
// ---------------------------------------------------------------------------

export type EventPage = ListPageBase & {
    type: PageType.EventPage;
    eventTemplateId: Uuid;
};

export type EventWithEventContainerPage = ListPageBase & {
    type: PageType.EventWithEventContainer;
    /** Body `templateId` for events+eventContainers APIs. */
    eventTemplateId: Uuid;
};

/** Dedicated Dulux quotation workspace grouped by Blue Store Lead Event Container. */
export type DuluxBlueStoreQuotationsPage = {
    id: Uuid;
    type: PageType.DuluxBlueStoreQuotations;
    title: string;
    ctaSlots: {
        createQuotation: Uuid;
        createQuotationFromRfq: Uuid;
        createCustomQuotation: Uuid;
        createCustomQuotationFromRfq: Uuid;
        reviseQuotation: Uuid;
        reviseCustomQuotation: Uuid;
        viewQuotation: Uuid;
        viewCustomQuotation: Uuid;
    };
};

export type EventContainerWithEventsPage = ListPageBase & {
    type: PageType.EventContainerWithEvents;
    /** Body `templateId` for eventContainers+events APIs. */
    eventContainerTemplateId: Uuid;
};

/** Product catalogue list. Chrome matches {@link EventContainerWithEventsPage}. */
export type TuffexProductPage = ListPageBase & {
    type: PageType.TuffexProduct;
};

export type TuffexProductCategoryPage = ListPageBase & {
    type: PageType.TuffexProductCategory;
};

export type TuffexProductTypePage = ListPageBase & {
    type: PageType.TuffexProductType;
};

export type TuffexProductBrandPage = ListPageBase & {
    type: PageType.TuffexProductBrand;
};

export type TuffexProductReviewQueuePage = ListPageBase & {
    type: PageType.TuffexProductReviewQueue;
};

export type AgentViewPage = ListPageBase & {
    type: PageType.AgentView;
    profilePanel: AgentViewProfilePanel;
};

/** All list-style pages — prefer narrowing with `isEventPage` / `isAgentViewPage` / etc. */
export type ListPage =
    | EventPage
    | EventWithEventContainerPage
    | EventContainerWithEventsPage
    | AgentViewPage
    | DuluxMyDayPlanPage
    | TuffexProductPage
    | TuffexProductCategoryPage
    | TuffexProductTypePage
    | TuffexProductBrandPage
    | TuffexProductReviewQueuePage;

export type AgentHomePage = {
    id: Uuid;
    type: PageType.AgentHomePage;
    title: string;
    dayPlanPageId: Uuid;
    description: string | null;
    sections: Array<AgentHomeSection>;
};

export type DuluxAgentTimelinessConfig = {
    delayLabel: string;
    dueAtColumnId: Uuid;
    dueInLabel: string;
};

export type DuluxAgentYourQueueCategoryConfig = {
    dueCategoryId: Uuid;
    id: Uuid;
    label: string;
    variant: "emergency" | "atRisk" | "hot" | "warm" | "cold";
};

export type DuluxAgentFieldValueFilter = {
    fieldId: Uuid;
    operator?: "in" | "isnull" | "isnotnull";
    values: Array<string>;
};

export type DuluxAgentTodayWorkCardConfig = {
    displayName: string;
    filters: Array<DuluxAgentFieldValueFilter>;
    id: Uuid;
    infoText: string | null;
    onSelect: AgentHomeBoxSelectAction;
};

export type DuluxAgentHomeSection =
    | {
          cards: Array<DuluxAgentTodayWorkCardConfig>;
          titleHistorical: string;
          titleToday: string;
          type: "todayWork";
      }
    | {
          categories: Array<DuluxAgentYourQueueCategoryConfig>;
          categoryColumnId: Uuid;
          defaultCategoryId: Uuid;
          hideListWhenHistorical: boolean;
          previewLimit: number;
          titleHistorical: string | null;
          titleToday: string | null;
          type: "yourQueue";
      }
    | {
          groups: Array<AgentHomeEngagementGroupConfig>;
          title: string | null;
          type: "engagementHealth";
      }
    | {
          cards: Array<AgentHomePerformanceCardConfig>;
          title: string | null;
          type: "performanceOverview";
      };

export type DuluxAgentHomePage = {
    dayPlanPageId: Uuid;
    description: string | null;
    domainAggregateTemplateId: Uuid;
    id: Uuid;
    sections: Array<DuluxAgentHomeSection>;
    timeliness: DuluxAgentTimelinessConfig;
    title: string;
    type: PageType.DuluxAgentHomePage;
};

export type DuluxMyDayPlanPage = ListPageBase & {
    domainAggregateTemplateId: Uuid;
    profilePanel: AgentViewProfilePanel;
    timeliness: DuluxAgentTimelinessConfig;
    type: PageType.DuluxMyDayPlanPage;
};

/**
 * Centered empty home: workspace name + fixed attribution line.
 * Subtitle defaults to a powered-by line when null at runtime.
 */
export type WorkspaceBrandingHomePage = {
    id: Uuid;
    type: PageType.WorkspaceBrandingHome;
    title: string;
    /** Override attribution line; null → default powered-by text. */
    subtitle: string | null;
};

export type DashboardPage = {
    id: Uuid;
    type: PageType.DashBoard;
    title: string;
    dateRange: {
        defaultPreset: "lastOneMonth";
    };
    filters: {
        scopeAgentToCurrentUser: boolean;
        useUserHierarchy: boolean;
    };
    sections: Array<{
        id: Uuid;
        title: string | null;
        widgets: Array<{id: Uuid; displayName: string | null}>;
    }>;
};

export type LeaderboardVisibleFilterAttribute = {
    attributeId: Uuid;
    resolverId: Uuid | null;
    /** Chip label. `null` / omitted → dataset column `displayName`. */
    label?: string | null;
};

export type LeaderboardPageWidget = {
    defaultDatasetId: Uuid | null;
    defaultEntityType: Uuid | null;
    defaultEntityDimensionId: Uuid | null;
    defaultRankingMetricId: Uuid | null;
    /** Empty when unused. Omitted on older configs is treated as none. */
    defaultSecondaryMetricIds?: Array<Uuid>;
    defaultTopN: number;
    showPodium: boolean;
    showConfigBar: boolean;
    allowCreate: boolean;
    allowEdit: boolean;
    allowDelete: boolean;
    allowExport: boolean;
    allowedEntityTypes: Array<Uuid> | null;
    allowedBoardIds: Array<Uuid> | null;
};

export type LeaderboardPage = {
    id: Uuid;
    type: PageType.Leaderboard;
    title: string;
    dateRange: {
        defaultPreset: "lastOneMonth";
    };
    filters: {
        scopeAgentToCurrentUser: boolean;
        useUserHierarchy: boolean;
        /**
         * `null` / omitted → Amaron / Emotorad / default filter chips.
         * `[]` → date range only.
         * Entries → one chip per dataset attribute (plus date range).
         */
        visibleFilterAttributeIds?: Array<LeaderboardVisibleFilterAttribute> | null;
    };
    widget: LeaderboardPageWidget;
};

export type FrontendLeaderboardPageWidget = {
    datasetId: Uuid;
    entityDimensionId: Uuid;
    rankingMetricId: Uuid;
    secondaryMetricIds: Array<Uuid>;
    topN: number;
    sortDirection: "asc" | "desc";
    showPodium: boolean;
};

export type FrontendLeaderboardPage = {
    id: Uuid;
    type: PageType.FrontendLeaderboard;
    title: string;
    dateRange: {
        defaultPreset: "lastOneMonth";
        timezone: string;
        granularity: "Day" | "Month" | "Year";
    };
    filters: {
        scopeAgentToCurrentUser: boolean;
        useUserHierarchy: boolean;
        /**
         * `null` → date range only.
         * `[]` → date range only.
         * Entries → one chip per dataset attribute (plus date range).
         */
        visibleFilterAttributeIds: Array<LeaderboardVisibleFilterAttribute> | null;
    };
    widget: FrontendLeaderboardPageWidget;
};

export type DispositionTrackerPage = {
    id: Uuid;
    type: PageType.DispositionTracker;
    title: string;
    /** Same catalog as list pages — typically EventContainerWithEvent kind. */
    dataSourceId: Uuid;
    levelFieldIds: {
        1: Uuid;
        2: Uuid;
        3: Uuid;
        4: Uuid;
    };
          dispositionLevel: AgentHomeDispositionLevelConfig;
    dateRange: {
        defaultPreset: "lastOneMonth" | "none";
        fieldId: Uuid;
    };
    filters: {
        requireLeadOwner: boolean;
        defaultJourneyScope: "active" | "all";
        scopeFilterEnabled: boolean;
        visibleFilterIds: Array<Uuid>;
        defaultApplied: Array<FilterItem>;
    };
    defaultSort: Array<SortItem>;
    search: {
        placeholder: string;
        fieldIds: Array<Uuid>;
    };
    kanban: {
        pageSize: number;
        columnMinWidthPx: number;
        columnGapPx: number;
    };
    card: {
        primaryCtaIds: Array<Uuid>;
        showPhoneDial: boolean;
        layout: CardLayout | null;
    };
    toolbarMoreCtaIds: Array<Uuid>;
};

export type FormPage = {
    id: Uuid;
    type: PageType.FormPage;
    title: string;
    formId: Uuid;
    parentPageId: Uuid | null;
};

/**
 * Workspace hosted shell under `/$pageKey`.
 * Config is type + title only — {@link type} selects `hostedPages/*` RouteComponent.
 * Do not use for list / AgentHome / Dashboard / Form / JourneyProfile pages.
 */
export type HostedPageType =
    | PageType.UserInvitation
    | PageType.Users
    | PageType.UserTemplates
    | PageType.AccountSettings
    | PageType.OrganizationalUnits
    | PageType.OrganizationalUnitTemplates
    | PageType.Notifications
    | PageType.Tickets
    | PageType.Billing
    | PageType.Profile
    | PageType.DataConnections
    | PageType.MyDataConnections
    | PageType.DataSourceDetail
    | PageType.ConnectDataSources
    | PageType.Products
    | PageType.ProductInventories
    | PageType.ProductCatalogueTemplates
    | PageType.RetailLocations
    | PageType.RetailLocationMicroStores
    | PageType.RetailLocationMicroStoreDefaultPages
    | PageType.StoreConfiguration
    | PageType.StoreConfigurationEdit
    | PageType.BlueStores
    | PageType.InviteBlueStores
    | PageType.DuluxStoreOwnerUserInvites
    | PageType.DuluxStoreOwnerUserInvite
    | PageType.DuluxBlueStoreQuotationCreate
    | PageType.DuluxBlueStoreQuotationDetail
    | PageType.DuluxBlueStoreQuotationRevise
    | PageType.DuluxBlueStoreCustomQuotationCreate
    | PageType.DuluxBlueStoreCustomQuotationDetail
    | PageType.DuluxBlueStoreCustomQuotationRevise
    | PageType.Reports
    | PageType.FavouriteReports
    | PageType.Collections
    | PageType.CollectionDetail
    | PageType.ReportDetail
    | PageType.ReportEdit
    | PageType.OttokartLeads
    | PageType.OttokartCustomers
    | PageType.OttokartProducts
    | PageType.OttokartProductReviewQueue
    | PageType.OttokartProductCategories
    | PageType.OttokartProductTypes
    | PageType.OttokartProductBrands
    | PageType.OttokartCollections
    | PageType.OttokartMachines
    | PageType.OttokartMachineTypes
    | PageType.OttokartMachineReviewQueue
    | PageType.OttokartOemParts
    | PageType.OttokartManufacturers
    | PageType.OttokartQuotations
    | PageType.OttokartAdvancedQuotations
    | PageType.OttokartInventory
    | PageType.OttokartQuotationDetail
    | PageType.OttokartViewQuotation
    | PageType.OttokartReviseQuotation
    | PageType.OttokartCreateQuotation
    | PageType.OttokartDuplicateQuotation
    | PageType.OttokartAdvancedQuotationDetail
    | PageType.OttokartAdvancedViewQuotation
    | PageType.OttokartAdvancedReviseQuotation
    | PageType.OttokartAdvancedCreateQuotation
    | PageType.OttokartAdvancedDuplicateQuotation
    | PageType.OttokartCreateProduct
    | PageType.OttokartEditProduct
    | PageType.OttokartViewProduct
    | PageType.OttokartEditVariant
    | PageType.OttokartHome
    | PageType.OttokartQuotationDashboard
    | PageType.OttokartInventoryDashboard
    | PageType.MediaLibrary
    | PageType.Blogs
    | PageType.DuluxLocationPages
    | PageType.StableLinks
    | PageType.IntellsysDashboard
    | PageType.AmazonDashboard
    | PageType.GoogleDashboard
    | PageType.MetaDashboard
    | PageType.DuluxDashboard
    | PageType.AmaronSourceWiseLeadFunnel
    | PageType.AmaronCampaignOverview
    | PageType.AmaronBcgGoogleDashboard
    | PageType.AmaronBcgMetaDashboard
    | PageType.AmaronBusinessSummary
    | PageType.TransferForm
    | PageType.TransferPreview;

export type HostedPage = {
    id: Uuid;
    type: HostedPageType;
    title: string;
    /**
     * Sidebar / header parent for deep-link shells (report detail, collection
     * detail and data-source detail). Required so the scaffold keeps
     * chrome instead of bouncing to home.
     */
    parentPageId?: Uuid | null;
    /**
     * Named action slots consumed by the hosted page implementation.
     * Every navigational control must resolve a CTA from this map.
     */
    ctaSlots?: Record<string, Uuid> | null;
    /** When true, Workspace Test omits app rail / header / module sidebar for this page. */
    hidePageScaffoldChrome?: boolean;
    /** Required by Retail Location Micro Store, Default Pages, Store Configuration and Store Configuration Edit to scope them to one Retail Location Template. */
    retailLocationTemplateId?: Uuid;
};

/** @deprecated Prefer {@link HostedPage}. */
export type UserInvitationPage = HostedPage & {type: PageType.UserInvitation};
/** @deprecated Prefer {@link HostedPage}. */
export type UsersPage = HostedPage & {type: PageType.Users};

/**
 * Shared Lead-profile chrome (tabs, footer CTAs, Events tab engine).
 * Used by the full {@link JourneyProfilePage} and by {@link JourneyProfileComponent} separately —
 * do not nest aside chrome inside the page.
 */
export type JourneyProfileChrome = {
    profilePanel: JourneyProfilePanel;
    /**
     * Events tab chrome (view switcher, All / Display by, sort, row/card CTAs).
     * Same contract as list-page {@link PageViewEngine}.
     */
    eventsViewEngine: PageViewEngine | null;
    /** Initial Events-tab sort. `null` → host default (`createdAt` ascending). */
    eventsDefaultSort: Array<SortItem> | null;
    /**
     * When `false`, the Events tab does not apply the last-30-days created-at range.
     * `null` / omitted → host default (`true`, last 30 days).
     */
    eventsDefaultCreatedAtMonthFilter?: boolean | null;
};

/** @deprecated Alias — prefer {@link JourneyProfileChrome}. */
export type JourneyProfilePresentationConfig = JourneyProfileChrome;

/**
 * Full-route Lead profile (`/$pageKey?journeyId=`).
 * Page-only — aside chrome lives in {@link WorkspaceConfig.components}.
 */
export type JourneyProfilePage = {
    id: Uuid;
    type: PageType.JourneyProfile;
    title: string;
    parentPageId: Uuid | null;
    /**
     * Event-container template whose disposition UDO is this page's L1–L4 catalog.
     * Omitted → first config entry with `provideDisposition: true`.
     */
    dispositionProviderEventContainerTemplateId?: Uuid;
} & JourneyProfileChrome;

/**
 * Non-form UI components (Lead profile aside, etc.).
 * Form drawers stay in {@link forms} with `layout.presentation: "drawer"` / CTA `openForm`.
 */
export enum ComponentType {
    JourneyProfile = "a9b0c1d2-e3f4-4567-8901-23456789abcd",
}

/**
 * Lead profile right aside — opened via CTA `openComponent`.
 * Expand navigates to {@link pageId} (a {@link JourneyProfilePage}).
 */
export type JourneyProfileComponent = {
    id: Uuid;
    type: ComponentType.JourneyProfile;
    title: string;
    /** Full Lead profile page opened from the aside expand control. */
    pageId: Uuid;
} & JourneyProfileChrome;

export type ConfigComponent = JourneyProfileComponent;

export type Page =
    | EventPage
    | EventWithEventContainerPage
    | DuluxBlueStoreQuotationsPage
    | EventContainerWithEventsPage
    | TuffexProductPage
    | TuffexProductCategoryPage
    | TuffexProductTypePage
    | TuffexProductBrandPage
    | TuffexProductReviewQueuePage
    | AgentViewPage
    | AgentHomePage
    | DuluxAgentHomePage
    | DuluxMyDayPlanPage
    | WorkspaceBrandingHomePage
    | DashboardPage
    | LeaderboardPage
    | FrontendLeaderboardPage
    | DispositionTrackerPage
    | FormPage
    | JourneyProfilePage
    | HostedPage;

type ColumnCount = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;

/**
 * Column density via CSS **container queries** (parent box width), not the viewport.
 *
 * Put a {@link ResponsiveColumnsContainer} (or any `container-type: inline-size` ancestor)
 * around the grid. Named `sm` / `md` / `lg` / `xl` steps use
 * {@link ResponsiveColumns.breakpoints} when set, else defaults (24 / 28 / 32 / 36 rem).
 * Use `at` for one-off px/rem thresholds (optional `maxWidth`).
 *
 * @example
 * ```json
 * {
 *   "DEFAULT": 1,
 *   "sm": 2,
 *   "xl": 3,
 *   "breakpoints": { "sm": "600px", "xl": "56rem" },
 *   "at": [{ "minWidth": "900px", "maxWidth": "1200px", "columns": 3 }]
 * }
 * ```
 */
export type ResponsiveColumns = {
    DEFAULT: ColumnCount | null;
    sm: ColumnCount | null;
    md: ColumnCount | null;
    lg: ColumnCount | null;
    xl: ColumnCount | null;
    /**
     * Container min-widths for named steps (CSS `px` | `rem` | `em` | `%`).
     * Measured on the nearest `container-type: inline-size` ancestor — not the screen.
     */
    breakpoints?: {
        sm?: string | null;
        md?: string | null;
        lg?: string | null;
        xl?: string | null;
    } | null;
    at: Array<{
        minWidth: string;
        maxWidth: string | null;
        columns: ColumnCount;
    }> | null;
};

type FormDefaultValueKind = "static" | "user" | "userTemplate" | "timestamp" | "duration";

export type FormFieldConstraint =
    | {
          type: "Time";
          refFieldId: Uuid;
          duration: string;
      }
    | {
          type: "Status";
          entity: "event" | "eventContainer";
          refFieldId: Uuid;
          disallowedStatusIds: Array<Uuid>;
      };

//TODO: add validation and its error message things
type FormFieldBase = {
    fieldId: Uuid;
    label: string | null;
    placeholder: string | null;
    isRequired: boolean;
    isVisible: boolean;
    isReadOnly: boolean;
    requiredWhen: Conditions | null;
    readonlyWhen: Conditions | null;
    visibleWhen: Conditions | null;
    /** Semantic role for specialized flows (e.g. cancel remarks). */
    fieldRole: "default" | "cancelRemarks" | null;
    defaultValueKind: FormDefaultValueKind | null;
    defaultValue: string | number | boolean | null;
    constraints: Array<FormFieldConstraint> | null;
};

/** Plain controls — no presentation-specific extras. */
type FormFieldPlainPresentation =
    | "singleLineText"
    | "multiLineText"
    | "select"
    | "dateTime"
    | "user"
    | "role"
    | "number"
    | "checkbox"
    | "journeyPicker"
    | "productPicker"
    | "organizationalUnitPicker"
    | "dispositionPath"
    | "scheduleCallDate"
    | "scheduleCallTimeSlot";

export type FormField =
    | (FormFieldBase & {
          presentation: FormFieldPlainPresentation;
      })
    | (FormFieldBase & {
          presentation: "contactNumber";
          dialCode: string | null;
      })
    | (FormFieldBase & {
          presentation: "accountSelector";
          dialCode: string | null;
          /** Sibling field updated when an account is selected. */
          customerNameFieldId: Uuid;
          accountPrefillCityColumnId: Uuid | null;
          accountPrefillStateColumnId: Uuid | null;
      })
    | (FormFieldBase & {
          /** Lead / journey picker. Selected value is the event-container id. */
          presentation: "journeySelector";
          contactNumberFieldId: Uuid;
      })
    | (FormFieldBase & {
          /** Accessible-OU dropdown; selected value is the OU id. */
          presentation: "ouSelector";
          ouTemplateId: Uuid;
          eventContainerTemplateId: Uuid;
          /** When set, the selector shows this OU and is read-only. */
          prefilledOuId: Uuid | null;
      })
    | (FormFieldBase & {
          presentation: "geographyPincode" | "geographyCity" | "geographyState";
          /**
           * Parent field to inherit from (usually a `geographyPincode` fieldId).
           * `null` → independent select.
           */
          inheritFromFieldId: Uuid | null;
      })
    | (FormFieldBase & {
          /** India geography master picker. Selected value is the master row UUID. */
          presentation: "geography";
          geographyKind: "pincode" | "city" | "state" | "zone";
          /**
           * Parent field to inherit from (usually a `geography` pincode fieldId).
           * `null` → independent select.
           */
          inheritFromFieldId: Uuid | null;
      })
    | (FormFieldBase & {
          /** Inherit presentation from the catalog field. */
          presentation: null;
      });

type FormSectionBase = {
    id: Uuid;
    title: string;
    iconId: Uuid | null;
    iconColorToken: string | null;
    iconBackgroundColorToken: string | null;
    initialIsOpen: boolean;
};

/**
 * Section chrome — kind-specific props only on the matching arm
 * (`fieldRows` for fields/disposition; notes extras for notes).
 */
export type FormSection =
    | (FormSectionBase & {
          kind: "fields" | null;
          fieldRows: Array<Array<FormField>>;
      })
    | (FormSectionBase & {
          kind: "disposition";
          fieldRows: Array<Array<FormField>>;
      })
    | (FormSectionBase & {
          kind: "notes";
          /** Label on journey-scoped notes (e.g. `"Lead"`). `null` → host default. */
          eventContainerSourceLabel: string | null;
      });

/** Sections that render editable field rows (excludes `kind: "notes"`). */
export type FormSectionWithFieldRows = Extract<FormSection, {kind: "fields" | "disposition" | null}>;

export type FormFieldConfig = FormSectionWithFieldRows["fieldRows"][number][number];

export function formSectionHasFieldRows(section: FormSection): section is FormSectionWithFieldRows {
    return section.kind !== "notes";
}

export function formFieldHasGeographyPresentation(
    formField: FormField,
): formField is Extract<
    FormField,
    {presentation: "geography" | "geographyPincode" | "geographyCity" | "geographyState"}
> {
    return (
        formField.presentation === "geography" ||
        formField.presentation === "geographyPincode" ||
        formField.presentation === "geographyCity" ||
        formField.presentation === "geographyState"
    );
}

export type FormSubmitCommand =
    | {
          type: "command";
          commandId:
              | "workspace.events.create"
              | "workspace.events.update"
              | "workspace.eventContainers.create"
              | "workspace.eventContainers.update"
              | "workspace.events.archive"
              | "workspace.additionalButton.cancel"
              | "workspace.additionalButton.log";
      }
    | {
          type: "workflow";
          /** Workspace-specific workflow registry key; resolved by frontend adapter. */
          workflowId: string;
      };

export type FormNavigateTo =
    | {type: "back"}
    | {type: "parentPage"}
    | {type: "stay"}
    | {type: "page"; pageKey: string; mode: string | null};

export type FormSuccessBehavior = {
    invalidateDataViewer: boolean;
    navigateTo: FormNavigateTo | null;
    toast: {
        title: string;
        appearance: "success" | "error" | "warning";
    } | null;
};

export type FormPrefill = {
    fromRow: boolean;
    fromQueryParams: Array<FormQueryParam> | null;
    /** When no event-container exists, allow creating one during submit. */
    allowCreateEventContainer: boolean;
};

type FormOverviewStatusColor = {
    backgroundColorToken: string;
    textColorToken: string;
    borderColorToken: string | null;
};

/** Status pill; `colors` keyed by stored option UUID of {@link fieldId}. */
type FormOverviewStatus = {
    fieldId: Uuid;
    colors: Record<string, FormOverviewStatusColor>;
};

type FormOverviewMetaItem =
    | {
          type: "field";
          fieldId: Uuid;
          icon: "calendar" | "tag" | "clock" | null;
      }
    | {
          type: "system";
          systemValue: "event.createdAt" | "journey.createdAt";
          icon: "calendar" | "tag" | "clock" | null;
      };

type FormOverviewBase = {
    status: FormOverviewStatus | null;
    titleFieldId: Uuid | null;
    /** When true, title link opens the workspace `JourneyProfile` page (`/$pageKey?journeyId=`). */
    titleLinkToJourney: boolean;
    /** Edit pencil CTA id (`openForm` → edit form). `null` → no pencil. */
    editCtaId: Uuid | null;
    meta: Array<FormOverviewMetaItem> | null;
    assignee: {
        fieldId: Uuid;
        badgeLabel: string | null;
    } | null;
};

/** Header chrome above form field sections — split by overview kind. */
export type FormOverviewConfig =
    | (FormOverviewBase & {kind: "eventOverview"})
    | (FormOverviewBase & {kind: "eventContainerOverview"});

type FormLayout = {
    presentation: "page" | "aside" | "drawer" | "modal" | "inline";
    /** `null` → 1 column. */
    columns: ResponsiveColumns | null;
    overview: FormOverviewConfig | null;
    sections: Array<FormSection>;
};

type FormShared = {
    id: Uuid;
    title: string;
    layout: FormLayout;
    prefill: FormPrefill | null;
    globalConstraints: Array<FormFieldConstraint> | null;
    /**
     * Event-container template whose disposition UDO is this form's log/dispose catalog.
     * Omitted → first config entry with `provideDisposition: true`.
     */
    dispositionProviderEventContainerTemplateId?: Uuid;
};

/** `eventTemplateId` required when the form writes an event template. */
type FormTargetEntity =
    | {
          targetEntity: "event" | "eventWithEventContainer";
          eventTemplateId: Uuid;
      }
    | {
          targetEntity: "eventContainer";
          eventTemplateId: null;
      };

type FormMutatingMode =
    | "create"
    | "update"
    | "log"
    | "schedule"
    | "scheduleModal"
    | "book"
    | "cancel"
    | "archive"
    | "disposition";

type FormViewSubmit = {
    label: string;
    cancelLabel: null;
    mutation: null;
    success: FormSuccessBehavior | null;
};

type FormMutatingSubmit = {
    label: string;
    cancelLabel: string | null;
    mutation: FormSubmitCommand;
    success: FormSuccessBehavior | null;
    /** When true, primary submit stays disabled until required fields are complete. Defaults to true for mutating forms. */
    gateSubmitUntilRequiredFieldsComplete?: boolean | null;
    /** Tooltip when submit is disabled due to incomplete required fields. */
    incompleteSubmitTooltip?: string | null;
};

/**
 * Exhaustive form contract. Split by:
 * - `mode: "view"` → no mutation (close/navigate only)
 * - mutating modes → required {@link FormSubmitCommand}
 * - `targetEntity` → whether `eventTemplateId` is required
 */
export type Form = FormTargetEntity &
    (
        | (FormShared & {
              mode: "view";
              submit: FormViewSubmit;
          })
        | (FormShared & {
              mode: FormMutatingMode;
              submit: FormMutatingSubmit;
          })
    );

export type FormType = Record<Uuid, Form>;

// ---------------------------------------------------------------------------
// Scaffold chrome (sidebar / header / bottom bar) — keyed by user template
// ---------------------------------------------------------------------------

export type LegacyNavIcon =
    | "home"
    | "dayPlan"
    | "dashboard"
    | "personWarning"
    | "textRephrase"
    | "talent"
    | "personAdd"
    | "phone"
    | "tools"
    | "peopleGroup"
    | "workItems"
    | "reports"
    | "dataFlow"
    | "library"
    | "shapes"
    | "pages"
    | "database"
    | "component"
    | "officeBuilding"
    | "image"
    | "bookBookmark"
    | "personOffboard";

/** Legacy name or an `iconRegistry` UUID pointing at an Atlassian Icon Explorer icon. */
export type NavIcon = LegacyNavIcon | Uuid;

export type NavTarget =
    | {
          type: "page";
          pageId: Uuid;
          moduleStatusHref: boolean;
      }
    | {
          type: "absolutePath";
          pathTemplate: string;
      };

type NavNestType = "list" | "collapsibleList" | "accordion";

export type NestPageRef = {
    pageId: Uuid;
    label: string;
    icon: NavIcon | null;
    argus?: ArgusClickConfig | null;
};

/**
 * Sidebar status-nav child for {@link NavNest} `kind: "status"`.
 * `possibleValues` must be a subset of the option values for `statusColumnId`.
 */
export type StatusNavStatus = {
    /** Stable nav status id (UUID v4) — not necessarily a column option value. */
    id: Uuid;
    label: string;
    /** Design-token name (e.g. `color.text.accent.blue`) or CSS color for the status icon. */
    color: string;
    /** Column values that map to this nav status. */
    possibleValues: Array<string>;
};

export type NavNest =
    | {
          kind: "status";
          nestType: NavNestType;
          statusColumnId: string;
          statuses: Array<StatusNavStatus>;
      }
    | {
          kind: "views";
          nestType: NavNestType;
          views: Array<ViewType>;
      }
    | {
          kind: "pages";
          nestType: NavNestType;
          pages: Array<NestPageRef>;
      }
    | {
          kind: "tabs";
          pages: Array<NestPageRef>;
      };

export type NavItem =
    | {
          type: "divider";
          id: Uuid;
      }
    | {
          type: "sectionHeading";
          id: Uuid;
          label: string;
      }
    | {
          type: "link";
          id: Uuid;
          label: string;
          icon: NavIcon;
          target: NavTarget;
          nest: NavNest | null;
          /**
           * Pin this link to the mobile bottom bar. At most the first 3 pinned links
           * become tabs. When no link in a `bottomBar` is pinned, the first 3 links
           * are used instead. Pinned links still appear in the More drawer, so the
           * drawer can stay in authored order.
           */
          pinToMobileBar?: boolean;
          argus?: ArgusClickConfig | null;
      };

export type HeaderMenuLink = {
    id: Uuid;
    label: string;
    description: string | null;
    icon: NavIcon | null;
    target: NavTarget;
    argus?: ArgusClickConfig | null;
};

export type HeaderItem =
    | {
          type: "create";
          /**
           * Create menu for this role. When non-empty, these CTA ids are the menu.
           * When omitted, the shared Create dropdown and its role fallback are used.
           */
          ctaIds?: Array<Uuid> | null;
          argus?: ArgusClickConfig | null;
      }
    | {type: "notifications"; argus?: ArgusClickConfig | null}
    | {type: "tickets"; argus?: ArgusClickConfig | null}
    | {
          type: "settings";
          id: Uuid;
          title: string;
          items: Array<HeaderMenuLink>;
      }
    | {
          type: "teamSpace";
          id: Uuid;
          title: string;
          items: Array<HeaderMenuLink>;
      };

/** Sidebar chrome for one user template. */
export type SidebarType = Array<NavItem>;

/** Header chrome for one user template. */
export type HeaderType = Array<HeaderItem>;

/** Mobile bottom-nav chrome for one user template. */
export type BottomBarType = Array<NavItem>;

/**
 * Level-1 app rail entry.
 * An app uses either `modules` (module panel drives navigation) or `navigation` (app click navigates directly).
 */
export type ScaffoldApp = {
    id: Uuid;
    label: string;
    icon: NavIcon;
    modules?: SidebarType;
    navigation?: NavTarget;
    argus?: ArgusClickConfig | null;
};

/** Bottom-nav items for one app. Array order is the app order.
 * A single entry is treated as the shared bar for every app in the role. */
export type ScaffoldBottomBar = {
    id: Uuid;
    items: BottomBarType;
};

export type ScaffoldByUserTemplate = {
    /** Page opened when this role enters the workspace (`/w/{workspaceId}`). */
    entryPageId: Uuid;
    sidebar: Array<ScaffoldApp>;
    header: HeaderType;
    bottomBar: Array<ScaffoldBottomBar>;
};

/** Harness scaffold aliases (sidebar / header chrome). */
export type ScaffoldHeaderItem = HeaderItem;
export type ScaffoldHeaderMenuLink = HeaderMenuLink;
export type ScaffoldNavIcon = NavIcon;
export type ScaffoldNavItem = NavItem;
export type ScaffoldNavNest = NavNest;
export type ScaffoldNavTarget = NavTarget;
export type ScaffoldNestPageRef = NestPageRef;
export type ScaffoldStatusNavStatus = StatusNavStatus;
export type ScaffoldViewType = ViewType;
export type ScaffoldBottomBarApp = ScaffoldBottomBar;

/** Scaffold chrome keyed by user template id. */
export type Scaffold = Record<Uuid, ScaffoldByUserTemplate>;

export const WORKSPACE_APP_ID = "a1b2c3d4-e5f6-4789-a012-3456789abcde" as Uuid;
export const AMARON_WORKSPACE_ID = "0ede12a9-dd64-4087-aa3e-adbc5a5a26f5" as Uuid;
export const DULUX_WORKSPACE_ID = "e2e65da7-073d-4dee-a573-0080f1da8fa3" as Uuid;

/** Active config-page route context (`pageKey` lives in the path). */
export type ScaffoldRouteContext = {
    pageKey: string;
    view?: ViewType | string;
    statusId?: Uuid;
};

export type ScaffoldSearch = ScaffoldRouteContext;

export function findScaffoldApp(sidebar: Array<ScaffoldApp>, appId: Uuid): ScaffoldApp | undefined {
    return sidebar.find((app) => app.id === appId);
}

export function findScaffoldBottomBarItems(
    bottomBar: Array<ScaffoldBottomBar>,
    appId: Uuid,
): BottomBarType | undefined {
    const matched = bottomBar.find((entry) => entry.id === appId);
    if (matched !== undefined) {
        return matched.items;
    }
    const shared = bottomBar[0];
    if (shared !== undefined) {
        return shared.items;
    }
    return undefined;
}

/** First app in the ordered sidebar array. */
export function getDefaultScaffoldAppId(roleScaffold: Pick<ScaffoldByUserTemplate, "sidebar">): Uuid {
    const firstApp = roleScaffold.sidebar[0];
    if (firstApp !== undefined) {
        return firstApp.id;
    }
    return WORKSPACE_APP_ID;
}

// ---------------------------------------------------------------------------
// Workspace root
// ---------------------------------------------------------------------------

export type WorkspaceEventContainerTemplateRef = {
    id: Uuid;
    displayName: string;
    /** When true, this template's disposition UDO is the L1–L4 catalog. */
    provideDisposition?: boolean;
};

export function eventContainerTemplateProvidesDisposition(
    entry: WorkspaceEventContainerTemplateRef,
): boolean {
    const provideDisposition: unknown = entry.provideDisposition;
    return provideDisposition === true || provideDisposition === "true";
}

export type ConfigType = {
    eventTemplates: Array<{id: Uuid; displayName: string}>;
    eventContainerTemplates: Array<WorkspaceEventContainerTemplateRef>;
    /** Chrome keyed by user template — same shape as scaffold registries. */
    scaffold: Scaffold;
    pages: Record<Uuid, Page>;
    forms: FormType;
    ctas: Record<Uuid, Cta>;
    columnMapping: ColumnMapping;
    resolvers: Record<Uuid, Resolver>;
    dataSources: Record<Uuid, DataSource>;
    iconRegistry: Record<Uuid, IconRegistryEntry>;
    transfer?: WorkspaceTransferUiConfig;
};

// ---------------------------------------------------------------------------
// Field catalog binding (resolved for display / formatters)
// ---------------------------------------------------------------------------

export type FieldBinding =
    | {
          key: Uuid;
          label: string;
          entity: "event" | "eventContainer";
          kind: "additionalColumn";
          columnId: Uuid;
          formatter: FieldFormatter;
          resolverId: Uuid | null;
          dispositionLevel: DispositionMappingLevel | null;
          optionSource: FieldOptionSource | null;
          valueOptions: Record<
              string,
              {
                  label: string;
                  backgroundColorToken: string | null;
                  textColorToken: string | null;
                  borderColorToken: string | null;
              }
          > | null;
          emptyLabel?: string | null;
      }
    | {
          key: Uuid;
          label: string;
          entity: "event" | "eventContainer";
          kind: "fixedColumn";
          property: FixedColumnName;
          formatter: "text" | "dateTime" | "user" | "role" | "select";
          resolverId: Uuid | null;
          dispositionLevel: null;
          valueOptions: Record<
              string,
              {
                  label: string;
                  backgroundColorToken: string | null;
                  textColorToken: string | null;
                  borderColorToken: string | null;
              }
          > | null;
          emptyLabel?: string | null;
      };

export type ResolvedFrontendFieldColumn =
    | {
          type: "additionalColumn";
          entity: "event" | "eventContainer";
          columnId: Uuid;
          column: Uuid;
      }
    | {
          type: "fixedColumn";
          entity: "event" | "eventContainer";
          columnName: FixedColumnName;
          column: FixedColumnName;
      };

// ---------------------------------------------------------------------------
// Page narrowing (PageType UUID discriminants only)
// ---------------------------------------------------------------------------

export function isEventPage(page: WorkspacePage): page is EventPage {
    return page.type === PageType.EventPage;
}

export function isEventWithEventContainerPage(page: WorkspacePage): page is EventWithEventContainerPage {
    return page.type === PageType.EventWithEventContainer;
}

export function isDuluxBlueStoreQuotationsPage(page: WorkspacePage): page is DuluxBlueStoreQuotationsPage {
    return page.type === PageType.DuluxBlueStoreQuotations;
}

export function isEventContainerWithEventsPage(page: WorkspacePage): page is EventContainerWithEventsPage {
    return page.type === PageType.EventContainerWithEvents;
}

export function isTuffexProductPage(page: WorkspacePage): page is TuffexProductPage {
    return page.type === PageType.TuffexProduct;
}

export function isTuffexCatalogueListPage(
    page: WorkspacePage,
): page is
    | TuffexProductPage
    | TuffexProductCategoryPage
    | TuffexProductTypePage
    | TuffexProductBrandPage
    | TuffexProductReviewQueuePage {
    return (
        page.type === PageType.TuffexProduct ||
        page.type === PageType.TuffexProductCategory ||
        page.type === PageType.TuffexProductType ||
        page.type === PageType.TuffexProductBrand ||
        page.type === PageType.TuffexProductReviewQueue
    );
}

export function isAgentViewPage(page: WorkspacePage): page is AgentViewPage {
    return page.type === PageType.AgentView;
}

export function isAgentHomePage(page: WorkspacePage): page is AgentHomePage {
    return page.type === PageType.AgentHomePage;
}

export function isDuluxAgentHomePage(page: WorkspacePage): page is DuluxAgentHomePage {
    return page.type === PageType.DuluxAgentHomePage;
}

export function isDuluxMyDayPlanPage(page: WorkspacePage): page is DuluxMyDayPlanPage {
    return page.type === PageType.DuluxMyDayPlanPage;
}

export function isWorkspaceBrandingHomePage(page: WorkspacePage): page is WorkspaceBrandingHomePage {
    return page.type === PageType.WorkspaceBrandingHome;
}

export function isDashboardPage(page: WorkspacePage): page is DashboardPage {
    return page.type === PageType.DashBoard;
}

export function isLeaderboardPage(page: WorkspacePage): page is LeaderboardPage {
    return page.type === PageType.Leaderboard;
}

export function isFrontendLeaderboardPage(page: WorkspacePage): page is FrontendLeaderboardPage {
    return page.type === PageType.FrontendLeaderboard;
}

export function isDispositionTrackerPage(page: WorkspacePage): page is DispositionTrackerPage {
    return page.type === PageType.DispositionTracker;
}

export function isFormPage(page: WorkspacePage): page is FormPage {
    return page.type === PageType.FormPage;
}

export function isJourneyProfilePage(page: WorkspacePage): page is JourneyProfilePage {
    return page.type === PageType.JourneyProfile;
}

const HOSTED_PAGE_TYPES: ReadonlySet<string> = new Set<string>([
    PageType.UserInvitation,
    PageType.Users,
    PageType.UserTemplates,
    PageType.AccountSettings,
    PageType.OrganizationalUnits,
    PageType.OrganizationalUnitTemplates,
    PageType.Notifications,
    PageType.Tickets,
    PageType.Billing,
    PageType.Profile,
    PageType.DataConnections,
    PageType.MyDataConnections,
    PageType.DataSourceDetail,
    PageType.ConnectDataSources,
    PageType.Products,
    PageType.ProductInventories,
    PageType.ProductCatalogueTemplates,
    PageType.RetailLocations,
    PageType.RetailLocationMicroStores,
    PageType.RetailLocationMicroStoreDefaultPages,
    PageType.StoreConfiguration,
    PageType.StoreConfigurationEdit,
    PageType.BlueStores,
    PageType.InviteBlueStores,
    PageType.DuluxStoreOwnerUserInvites,
    PageType.DuluxStoreOwnerUserInvite,
    PageType.DuluxBlueStoreQuotationCreate,
    PageType.DuluxBlueStoreQuotationDetail,
    PageType.DuluxBlueStoreQuotationRevise,
    PageType.DuluxBlueStoreCustomQuotationCreate,
    PageType.DuluxBlueStoreCustomQuotationDetail,
    PageType.DuluxBlueStoreCustomQuotationRevise,
    PageType.Reports,
    PageType.FavouriteReports,
    PageType.Collections,
    PageType.CollectionDetail,
    PageType.ReportDetail,
    PageType.ReportEdit,
    PageType.OttokartLeads,
    PageType.OttokartCustomers,
    PageType.OttokartProducts,
    PageType.OttokartProductReviewQueue,
    PageType.OttokartProductCategories,
    PageType.OttokartProductTypes,
    PageType.OttokartProductBrands,
    PageType.OttokartCollections,
    PageType.OttokartMachines,
    PageType.OttokartMachineTypes,
    PageType.OttokartMachineReviewQueue,
    PageType.OttokartOemParts,
    PageType.OttokartManufacturers,
    PageType.OttokartQuotations,
    PageType.OttokartAdvancedQuotations,
    PageType.OttokartInventory,
    PageType.OttokartQuotationDetail,
    PageType.OttokartViewQuotation,
    PageType.OttokartReviseQuotation,
    PageType.OttokartCreateQuotation,
    PageType.OttokartDuplicateQuotation,
    PageType.OttokartAdvancedQuotationDetail,
    PageType.OttokartAdvancedViewQuotation,
    PageType.OttokartAdvancedReviseQuotation,
    PageType.OttokartAdvancedCreateQuotation,
    PageType.OttokartAdvancedDuplicateQuotation,
    PageType.OttokartCreateProduct,
    PageType.OttokartEditProduct,
    PageType.OttokartViewProduct,
    PageType.OttokartEditVariant,
    PageType.OttokartHome,
    PageType.OttokartQuotationDashboard,
    PageType.OttokartInventoryDashboard,
    PageType.MediaLibrary,
    PageType.Blogs,
    PageType.DuluxLocationPages,
    PageType.StableLinks,
    PageType.IntellsysDashboard,
    PageType.AmazonDashboard,
    PageType.GoogleDashboard,
    PageType.MetaDashboard,
    PageType.DuluxDashboard,
    PageType.AmaronSourceWiseLeadFunnel,
    PageType.AmaronCampaignOverview,
    PageType.AmaronBcgGoogleDashboard,
    PageType.AmaronBcgMetaDashboard,
    PageType.AmaronBusinessSummary,
    PageType.TransferForm,
    PageType.TransferPreview,
]);

/** True for hosted shell page type ids (config `type` UUID or reserved route slug). */
export function isHostedPageType(type: string): boolean {
    return HOSTED_PAGE_TYPES.has(type);
}

export function isHostedPage(page: WorkspacePage): page is HostedPage {
    return isHostedPageType(page.type);
}

/** True when workspace test should render this hosted page without scaffold chrome. */
export function shouldHidePageScaffoldChrome(page: WorkspacePage | undefined): boolean {
    if (page === undefined || isHostedPage(page) === false) {
        return false;
    }
    return page.hidePageScaffoldChrome === true;
}

export function isUserInvitationPage(page: WorkspacePage): page is UserInvitationPage {
    return page.type === PageType.UserInvitation;
}

export function isUsersPage(page: WorkspacePage): page is UsersPage {
    return page.type === PageType.Users;
}

export function isJourneyProfileComponent(
    component: ConfigComponent,
): component is JourneyProfileComponent {
    return component.type === ComponentType.JourneyProfile;
}

type RuntimeFilterItemShape = {
    type?: string;
    values?: Array<string>;
    value?: string | null;
    duration?: number;
};

/**
 * Resolve option UUID(s) from a {@link FilterItem}.
 * Also accepts stale architect / localStorage drafts that omit the `type`
 * discriminant but still carry `values` / `value` / `duration`.
 */
export function resolveFilterItemValues(filter: FilterItem): Array<string> {
    // Cast through unknown: stored drafts may omit the discriminant at runtime.
    const runtimeFilter = filter as unknown as RuntimeFilterItemShape;

    if (runtimeFilter.type === "multipleValues" || runtimeFilter.type === "userTemplate") {
        return Array.isArray(runtimeFilter.values) === true ? runtimeFilter.values : [];
    }
    if (runtimeFilter.type === "duration") {
        if (typeof runtimeFilter.duration !== "number") {
            return [];
        }
        return [String(runtimeFilter.duration)];
    }
    if (runtimeFilter.type === "singleValue") {
        if (runtimeFilter.value !== null && runtimeFilter.value !== undefined && runtimeFilter.value !== "") {
            return [runtimeFilter.value];
        }
        return [];
    }

    // Discriminant missing: infer from payload shape (common in older drafts).
    if (Array.isArray(runtimeFilter.values) === true) {
        return runtimeFilter.values;
    }
    if (typeof runtimeFilter.duration === "number") {
        return [String(runtimeFilter.duration)];
    }
    if (runtimeFilter.value !== null && runtimeFilter.value !== undefined && runtimeFilter.value !== "") {
        return [runtimeFilter.value];
    }
    return [];
}

/** First comparable value for UI that still expects a single scalar. */
export function resolveFilterItemScalarValue(filter: FilterItem): string | null {
    const values = resolveFilterItemValues(filter);
    return values[0] ?? null;
}

export function resolveFrontendFieldIdToColumn(
    columnMapping: ColumnMapping,
    fieldId: Uuid,
): ResolvedFrontendFieldColumn | null {
    const entry = columnMapping[fieldId];
    if (entry === undefined) {
        return null;
    }
    const entityKind = columnMappingEntityKind(entry.entity);
    if (entityKind === null) {
        return null;
    }
    if (entry.type === "additionalColumn") {
        return {
            type: "additionalColumn",
            entity: entityKind,
            columnId: entry.columnId,
            column: entry.columnId,
        };
    }
    return {
        type: "fixedColumn",
        entity: entityKind,
        columnName: entry.columnName,
        column: entry.columnName,
    };
}

export function flattenCardDetailCells(details: CardDetailGrid): Array<CardDetailCell> {
    return details.flat();
}

export function isCtaTableAction(value: unknown): value is CtaTableAction {
    return (
        typeof value === "string" &&
        (
            [
                "profile",
                "disposition",
                "edit",
                "view",
                "create",
                "logCall",
                "notes",
                "scheduleCall",
                "archive",
                "cancelCall",
            ] as Array<string>
        ).includes(value)
    );
}

export type WorkspacePage = Page;

export function isListStylePage(page: WorkspacePage): page is ListPage {
    return (
        page.type === PageType.EventPage ||
        page.type === PageType.EventWithEventContainer ||
        page.type === PageType.EventContainerWithEvents ||
        page.type === PageType.AgentView ||
        page.type === PageType.DuluxMyDayPlanPage ||
        page.type === PageType.TuffexProduct ||
        page.type === PageType.TuffexProductCategory ||
        page.type === PageType.TuffexProductType ||
        page.type === PageType.TuffexProductBrand ||
        page.type === PageType.TuffexProductReviewQueue
    );
}

/** Resolve a view arm from {@link PageViewEngine.views}. */
export function getPageView<T extends ViewType>(
    page: {viewEngine: PageViewEngine},
    viewType: T,
): Extract<PageView, {type: T}> | undefined {
    return page.viewEngine.views.find(
        (view): view is Extract<PageView, {type: T}> => view.type === viewType,
    );
}

type FieldBindingPage = ListPage | DuluxMyDayPlanPage;

const pageFieldBindingsCache = new WeakMap<FieldBindingPage, Record<string, FieldBinding>>();

function defaultFixedColumnFormatter(columnName: FixedColumnName): "text" | "dateTime" | "user" {
    if (columnName === "createdAt" || columnName === "updatedAt") {
        return "dateTime";
    }
    if (columnName === "createdBy" || columnName === "updatedBy") {
        return "user";
    }
    return "text";
}

const FIELD_FORMATTER_RANK: Record<FieldFormatter, number> = {
    text: 0,
    number: 1,
    date: 2,
    dateTime: 3,
    user: 4,
    role: 5,
    select: 6,
    disposition: 7,
};

/** Legacy drafts may omit `formatter` on mapping entries. */
type MutableColumnMappingEntry = {
    type: "additionalColumn" | "fixedColumn";
    columnId?: Uuid;
    columnName?: FixedColumnName;
    entity: ColumnMappingEntityRef;
    displayName: string;
    formatter?: FieldFormatter;
    optionSource?: FieldOptionSource;
    options?: Record<string, FieldOption> | null;
    level?: DispositionMappingLevel;
};

/**
 * Ensure every {@link ColumnMapping} entry has a formatter. Legacy drafts that
 * omit it are filled from list-page `fields` (richest wins). Entries that already
 * declare a formatter are left alone — page overrides happen in
 * {@link buildFieldBindings}.
 */
export function enrichColumnMappingFormatters(
    columnMapping: ColumnMapping,
    pages: Record<string, Page>,
): ColumnMapping {
    const out = structuredClone(columnMapping) as Record<string, MutableColumnMappingEntry>;
    const hadFormatter = new Set(
        Object.entries(columnMapping)
            .filter(([, entry]) => (entry as MutableColumnMappingEntry).formatter !== undefined)
            .map(([fieldId]) => fieldId),
    );

    for (const page of Object.values(pages)) {
        if (isListStylePage(page) !== true) {
            continue;
        }
        for (const field of page.fields) {
            const entry = out[field.id];
            if (entry === undefined || hadFormatter.has(field.id) === true) {
                continue;
            }
            const currentRank =
                entry.formatter === undefined
                    ? -1
                    : (FIELD_FORMATTER_RANK[entry.formatter] ?? -1);
            const nextRank = FIELD_FORMATTER_RANK[field.formatter] ?? 0;
            if (currentRank >= nextRank) {
                continue;
            }

            if (entry.type === "fixedColumn") {
                const columnName = entry.columnName ?? "id";
                if (field.formatter === "select" || field.formatter === "disposition") {
                    entry.formatter = field.formatter;
                    entry.options = "options" in field ? field.options : null;
                    continue;
                }
                entry.formatter =
                    field.formatter === "date"
                    || field.formatter === "dateTime"
                    || field.formatter === "user"
                        ? field.formatter
                        : defaultFixedColumnFormatter(columnName);
                continue;
            }

            if (field.formatter === "select" || field.formatter === "disposition") {
                entry.formatter = field.formatter;
                if (field.formatter === "select" && "optionSource" in field && field.optionSource !== undefined) {
                    entry.optionSource = field.optionSource;
                    delete entry.options;
                } else {
                    entry.options = "options" in field ? field.options : null;
                    delete entry.optionSource;
                }
                continue;
            }

            entry.formatter = field.formatter;
            delete entry.options;
            delete entry.optionSource;
        }
    }

    for (const entry of Object.values(out)) {
        if (entry.formatter !== undefined) {
            continue;
        }
        if (entry.type === "fixedColumn") {
            entry.formatter = defaultFixedColumnFormatter(entry.columnName ?? "id");
            continue;
        }
        entry.formatter = "text";
    }

    return out as ColumnMapping;
}

/**
 * Build bindings from workspace {@link ColumnMapping} (source of truth for every
 * frontend field id, including formatter). Page `fields` only override label /
 * formatter / select options for that page.
 */
function buildFieldBindings(page: FieldBindingPage, columnMapping: ColumnMapping): Record<string, FieldBinding> {
    const out: Record<string, FieldBinding> = {};

    for (const [fieldId, entry] of Object.entries(columnMapping)) {
        const entityKind = columnMappingEntityKind(entry.entity);
        if (entityKind === null) {
            continue;
        }
        if (entry.type === "additionalColumn") {
            if (entry.formatter === "select" || entry.formatter === "disposition") {
                out[fieldId] = {
                    key: fieldId as Uuid,
                    label: entry.displayName,
                    entity: entityKind,
                    kind: "additionalColumn",
                    columnId: entry.columnId,
                    formatter: entry.formatter,
                    resolverId: null,
                    dispositionLevel: resolveColumnMappingDispositionLevel(entry),
                    optionSource: "optionSource" in entry ? entry.optionSource ?? null : null,
                    valueOptions: "options" in entry ? entry.options ?? null : null,
                };
                continue;
            }
            out[fieldId] = {
                key: fieldId as Uuid,
                label: entry.displayName,
                entity: entityKind,
                kind: "additionalColumn",
                columnId: entry.columnId,
                formatter: entry.formatter,
                resolverId: null,
                dispositionLevel: null,
                optionSource: null,
                valueOptions: null,
            };
            continue;
        }
        if (entry.formatter === "select") {
            out[fieldId] = {
                key: fieldId as Uuid,
                label: entry.displayName,
                entity: entityKind,
                kind: "fixedColumn",
                property: entry.columnName,
                formatter: entry.formatter,
                resolverId: null,
                dispositionLevel: null,
                valueOptions: entry.options,
            };
            continue;
        }
        out[fieldId] = {
            key: fieldId as Uuid,
            label: entry.displayName,
            entity: entityKind,
            kind: "fixedColumn",
            property: entry.columnName,
            formatter:
                entry.formatter === "dateTime" || entry.formatter === "user" ? entry.formatter : "text",
            resolverId: null,
            dispositionLevel: null,
            valueOptions: null,
        };
    }

    for (const field of page.fields) {
        const base = out[field.id];
        if (base === undefined) {
            continue;
        }
        const pageHasSelectOptions = field.formatter === "select" && "options" in field;
        const pageValueOptions = pageHasSelectOptions === true ? field.options : null;
        const pageOptionSource =
            (field.formatter === "select" || field.formatter === "disposition") && "optionSource" in field
                ? field.optionSource ?? null
                : null;
        if (base.kind === "additionalColumn") {
            const optionSource = pageOptionSource ?? base.optionSource;
            out[field.id] = {
                ...base,
                label: field.label,
                formatter: field.formatter,
                resolverId: field.resolverId ?? null,
                dispositionLevel:
                    field.formatter === "disposition" ? base.dispositionLevel : null,
                optionSource: optionSource,
                valueOptions: optionSource === null ? pageValueOptions ?? base.valueOptions : null,
                ...(typeof field.emptyLabel === "string" && field.emptyLabel !== ""
                    ? {emptyLabel: field.emptyLabel}
                    : {}),
            };
            continue;
        }
        const pageFormatter = field.formatter;
        const fixedFormatter =
            pageFormatter === "dateTime" ||
            pageFormatter === "user" ||
            pageFormatter === "role" ||
            pageFormatter === "select"
                ? pageFormatter
                : base.formatter;
        out[field.id] = {
            ...base,
            label: field.label,
            formatter: fixedFormatter,
            resolverId: field.resolverId ?? null,
            dispositionLevel: null,
            valueOptions: pageValueOptions ?? base.valueOptions,
            ...(typeof field.emptyLabel === "string" && field.emptyLabel !== ""
                ? {emptyLabel: field.emptyLabel}
                : {}),
        };
    }

    return out;
}

/**
 * Attach resolved {@link FieldBinding}s for a list page.
 * Mapping comes from workspace {@link ColumnMapping}; page `fields` override
 * display (label / formatter / options). Call once when adapting runtime config.
 */
export function attachPageFieldBindings(page: FieldBindingPage, columnMapping: ColumnMapping): void {
    pageFieldBindingsCache.set(page, buildFieldBindings(page, columnMapping));
}

/**
 * Runtime field bindings for a page (full columnMapping + page overrides).
 * Requires {@link attachPageFieldBindings} (done in config runtime adapt).
 */
export function fieldBindingsById(page: FieldBindingPage): Record<string, FieldBinding> {
    return pageFieldBindingsCache.get(page) ?? {};
}

/**
 * Bindings for fields listed on this page only (API column requests, list UI).
 * Full {@link fieldBindingsById} includes every columnMapping entry.
 */
export function pageListedFieldBindings(page: FieldBindingPage): Array<FieldBinding> {
    const bindings = fieldBindingsById(page);
    return page.fields.flatMap((field) => {
        const binding = bindings[field.id];
        return binding === undefined ? [] : [binding];
    });
}

export function getListCardLayout(page: ListPage): CardLayout | null {
    return getPageView(page, "list")?.cardLayout ?? null;
}

export function getKanbanCardLayout(page: ListPage): CardLayout | null {
    return getPageView(page, "kanban")?.cardLayout ?? null;
}

export type WorkspaceTransferPreviewColumnKind =
    | "currentOwner"
    | "transferredOwner"
    | "transferredOn"
    | "currentOu"
    | "transferredOu"
    | "transferReason"
    | "customerName"
    | "contactNumber"
    | "status";

export type WorkspaceTransferPreviewColumnDef = {
    id: string;
    kind: WorkspaceTransferPreviewColumnKind;
    label: string;
};

export type WorkspaceTransferReasonOption = {
    id: string;
    label: string;
};

/**
 * Transfer UI constants on workspace UI config (`configuration.transfer`).
 * Dimensions, status eligibility, and roles come from Event Container Movement capability at runtime.
 */
export type WorkspaceTransferUiConfig = {
    bulkPreviewColumns?: Array<WorkspaceTransferPreviewColumnDef>;
    bulkSelectMaxTagCount?: number;
    listPageLabel: string;
    previewColumns: Array<WorkspaceTransferPreviewColumnDef>;
    reasonRequired?: boolean;
    reasons: Array<WorkspaceTransferReasonOption>;
    remarksRequired?: boolean;
    remarksRequiredWhenReasonId?: string;
    showDimensionSelector?: boolean;
};

export type WorkspaceConfig = {
    eventTemplates: Array<{id: Uuid; displayName: string}>;
    eventContainerTemplates: Array<WorkspaceEventContainerTemplateRef>;
    scaffold: Scaffold;
    pages: Record<string, Page>;
    /**
     * Non-form UI surfaces (e.g. Lead profile aside).
     * Not a home for form drawers — those stay in {@link forms} + CTA `presentation`.
     */
    components: Record<string, ConfigComponent>;
    forms: FormType;
    ctas: Record<string, Cta>;
    columnMapping: ColumnMapping;
    resolvers: Record<Uuid, Resolver>;
    dataSources: Record<Uuid, DataSource>;
    transfer?: WorkspaceTransferUiConfig;
};

export const duluxAgentTimelinessConfigValidator: zod.ZodType<DuluxAgentTimelinessConfig> = zod.strictObject({
    delayLabel: zod.string().min(1),
    dueAtColumnId: uuidZodValidator,
    dueInLabel: zod.string().min(1),
});

export const duluxAgentYourQueueCategoryConfigValidator: zod.ZodType<DuluxAgentYourQueueCategoryConfig> = zod.strictObject({
    dueCategoryId: uuidZodValidator,
    id: uuidZodValidator,
    label: zod.string().min(1),
    variant: zod.enum(["emergency", "atRisk", "hot", "warm", "cold"]),
});

export const duluxAgentFieldValueFilterValidator: zod.ZodType<DuluxAgentFieldValueFilter> = zod.strictObject({
    fieldId: uuidZodValidator,
    operator: zod.enum(["in", "isnull", "isnotnull"]).optional(),
    values: zod.array(zod.string()),
});

export const duluxAgentHomePageValidator = zod.strictObject({
    dayPlanPageId: uuidZodValidator,
    description: zod.string().nullable(),
    domainAggregateTemplateId: uuidZodValidator,
    id: uuidZodValidator,
    sections: zod.array(zod.union([
        zod.strictObject({
            cards: zod.array(zod.object({
                displayName: zod.string(),
                filters: zod.array(duluxAgentFieldValueFilterValidator).min(1),
                id: uuidZodValidator,
                infoText: zod.string().nullable().optional(),
                onSelect: zod.unknown().optional(),
            })),
            titleHistorical: zod.string(),
            titleToday: zod.string(),
            type: zod.literal("todayWork"),
        }),
        zod.strictObject({
            categories: zod.array(duluxAgentYourQueueCategoryConfigValidator).length(3),
            categoryColumnId: uuidZodValidator,
            defaultCategoryId: uuidZodValidator,
            hideListWhenHistorical: zod.boolean(),
            previewLimit: zod.number().int().positive(),
            titleHistorical: zod.string().nullable(),
            titleToday: zod.string().nullable(),
            type: zod.literal("yourQueue"),
        }),
        zod.strictObject({
            groups: zod.array(zod.object({
                id: uuidZodValidator,
                label: zod.string(),
                metrics: zod.array(zod.unknown()),
            })),
            title: zod.string().nullable(),
            type: zod.literal("engagementHealth"),
        }),
        zod.strictObject({
            cards: zod.array(zod.object({
                formula: zod.string(),
                id: zod.string(),
                label: zod.string(),
            })),
            title: zod.string().nullable(),
            type: zod.literal("performanceOverview"),
        }),
    ])),
    timeliness: duluxAgentTimelinessConfigValidator,
    title: zod.string(),
    type: zod.literal(PageType.DuluxAgentHomePage),
});

export const duluxMyDayPlanPageValidator = zod.object({
    dataSourceId: uuidZodValidator,
    domainAggregateTemplateId: uuidZodValidator,
    id: uuidZodValidator,
    timeliness: duluxAgentTimelinessConfigValidator,
    title: zod.string(),
    type: zod.literal(PageType.DuluxMyDayPlanPage),
}).passthrough();

export function findFormPageByFormId(config: WorkspaceConfig, formId: Uuid): FormPage | undefined {
    for (const page of Object.values(config.pages)) {
        if (page.type !== PageType.FormPage) {
            continue;
        }
        if (page.formId === formId) {
            return page;
        }
    }
    return undefined;
}
